import { HOSTED_CENTER_AGENT_CONTROLLER_REF, HOSTED_CENTER_AGENT_CONTROLLER_REF as HOSTED_CENTER_AGENT_CONTROLLER_REF$1, HOSTED_CENTER_AGENT_CONTROLLER_REF_ID, HOSTED_CENTER_AGENT_CONTROLLER_REF_ID as HOSTED_CENTER_AGENT_CONTROLLER_REF_ID$1, agentControllerPermissionModeSchema, openCodePermissionConfigSchema } from "@wakeloop/schemas";
//#region src/agent-controller-interface-display.ts
function createAgentControllerInterfaceDisplayRows(snapshot) {
	const rows = [
		{
			label: "Controller",
			value: formatAgentControllerStatus(snapshot.status)
		},
		{
			label: "Account",
			value: formatAgentControllerAccount(snapshot)
		},
		{
			label: "Native interface",
			value: formatAgentControllerNativeInterface(snapshot.nativeInterface)
		},
		{
			label: "Version",
			value: formatAgentControllerVersion(snapshot.version) ?? "Not reported"
		},
		{
			label: "Version status",
			value: formatAgentControllerVersionAdvisory(snapshot)
		},
		{
			label: "Launch check",
			value: formatAgentControllerLaunch(snapshot)
		},
		{
			label: "Models",
			value: formatAgentControllerModels(snapshot)
		},
		{
			label: "Capabilities",
			value: formatAgentControllerCapabilities(snapshot.capabilities)
		},
		{
			label: "Last checked",
			value: formatAgentControllerCheckedAt(snapshot.checkedAt)
		}
	];
	if (snapshot.skills.length > 0) rows.splice(7, 0, {
		label: "Skills",
		value: formatAgentControllerSkills(snapshot)
	});
	if (snapshot.message) rows.splice(1, 0, {
		label: "Message",
		value: snapshot.message
	});
	if (snapshot.versionAdvisory?.status === "behind_latest" && snapshot.versionAdvisory.updateCommand) {
		const launchRowIndex = rows.findIndex((row) => row.label === "Launch check");
		rows.splice(launchRowIndex >= 0 ? launchRowIndex : rows.length, 0, {
			label: "Update command",
			value: snapshot.versionAdvisory.updateCommand
		});
	}
	return rows;
}
function resolveAgentControllerInterfaceDescription(input) {
	if (!input.snapshot) return input.displayDescription;
	if (input.snapshot.message) return input.snapshot.message;
	if (input.snapshot.auth.status === "unauthenticated") return "Installed, but this controller is not signed in.";
	if (input.snapshot.status === "ready") return "WakeLoop can launch this controller on this computer.";
	if (input.snapshot.status === "warning") return "Installed, but WakeLoop found something that needs attention.";
	if (input.snapshot.status === "error") return "WakeLoop could not verify this controller.";
	if (input.snapshot.status === "unavailable") return "Detected, but not available to run right now.";
	return input.displayDescription;
}
function shouldExpandAgentControllerInterface(snapshot) {
	return Boolean(snapshot && (snapshot.status === "warning" || snapshot.status === "error" || snapshot.status === "unavailable" || snapshot.auth.status === "unauthenticated" || snapshot.launch.status === "needs_choice" || snapshot.launch.status === "needs_repair"));
}
function formatAgentControllerInterfaceSummary(snapshot) {
	const auth = formatAgentControllerAuthSummary(snapshot);
	const version = formatAgentControllerVersion(snapshot.version);
	if (auth && version) return `${auth} · ${version}`;
	return auth ?? version ?? formatAgentControllerStatus(snapshot.status);
}
function getAgentControllerVersionAdvisoryPresentation(snapshot) {
	const advisory = snapshot?.versionAdvisory;
	if (advisory?.status !== "behind_latest") return null;
	const latestVersion = formatAgentControllerVersion(advisory.latestVersion);
	return {
		detail: advisory.message ?? (latestVersion ? `A newer version is available: ${latestVersion}.` : "A newer controller version is available."),
		label: "Update available",
		tone: "warning",
		updateCommand: advisory.updateCommand
	};
}
function formatAgentControllerAuthSummary(snapshot) {
	if (snapshot.auth.status === "authenticated") {
		const account = resolveAgentControllerAuthDisplayLabel(snapshot);
		return account ? `Authenticated · ${account}` : "Authenticated";
	}
	if (snapshot.auth.status === "unauthenticated") return "Not authenticated";
	return null;
}
function formatAgentControllerVersion(version) {
	if (!version) return null;
	return version.startsWith("v") ? version : `v${version}`;
}
function formatAgentControllerVersionAdvisory(snapshot) {
	const advisory = snapshot.versionAdvisory;
	if (!advisory) return "Not checked";
	if (advisory.status === "current") {
		const latestVersion = formatAgentControllerVersion(advisory.latestVersion);
		return latestVersion ? `Up to date · ${latestVersion}` : "Up to date";
	}
	if (advisory.status === "behind_latest") {
		const latestVersion = formatAgentControllerVersion(advisory.latestVersion);
		return latestVersion ? `Update available · ${latestVersion}` : "Update available";
	}
	return advisory.message ?? "Could not check latest version";
}
function formatAgentControllerStatus(status) {
	switch (status) {
		case "ready": return "Enabled";
		case "warning": return "Needs attention";
		case "error": return "Error";
		case "unavailable": return "Unavailable";
		case "disabled": return "Disabled";
		default: return "Unknown";
	}
}
function formatAgentControllerAccount(snapshot) {
	if (snapshot.auth.status === "authenticated") return resolveAgentControllerAuthDisplayLabel(snapshot) ?? "Authenticated";
	if (snapshot.auth.status === "unauthenticated") return "Not authenticated";
	return "Unknown";
}
function resolveAgentControllerAuthDisplayLabel(snapshot) {
	const label = snapshot.auth.email ?? snapshot.auth.label;
	if (!label || isGenericAuthenticatedLabel(label)) return null;
	return label;
}
function isGenericAuthenticatedLabel(value) {
	const normalized = value.trim().toLowerCase();
	return normalized === "signed in" || normalized === "authenticated";
}
function formatAgentControllerNativeInterface(value) {
	switch (value) {
		case "codex-app-server": return "Codex App Server";
		case "claude-agent-sdk": return "Claude Agent SDK";
		case "claude-cli": return "Claude CLI";
		case "local-cli": return "Local CLI";
		default: return value;
	}
}
function formatAgentControllerLaunch(snapshot) {
	return `${formatLocalAgentLaunchStatus(snapshot.launch.status)} · ${snapshot.launch.commandPath ?? "No command path reported"} · ${snapshot.launch.installChoiceCount === 1 ? "1 install" : `${snapshot.launch.installChoiceCount} installs`}`;
}
function formatLocalAgentLaunchStatus(status) {
	switch (status) {
		case "ready": return "Can launch";
		case "needs_choice": return "Needs install choice";
		case "needs_repair": return "Needs repair";
		default: return "Not available";
	}
}
function formatAgentControllerModels(snapshot) {
	if (snapshot.models.length === 0) return "No native model list reported";
	const visibleModels = snapshot.models.slice(0, 4).map((model) => model.displayName);
	const remaining = snapshot.models.length - visibleModels.length;
	return remaining > 0 ? `${visibleModels.join(", ")} +${remaining} more` : visibleModels.join(", ");
}
function formatAgentControllerCapabilities(capabilities) {
	const labels = [
		capabilities.approvals ? "Approvals" : null,
		capabilities.cancel ? "Cancel" : null,
		capabilities.interrupt ? "Interrupt" : null,
		capabilities.modelSelection ? "Model selection" : null,
		capabilities.nativeEventStream ? "Native event stream" : null,
		capabilities.permissionModes ? "Permission modes" : null,
		capabilities.structuredUserInput ? "Structured user input" : null
	].filter((label) => Boolean(label));
	return labels.length > 0 ? labels.join(", ") : "No native controls reported";
}
function formatAgentControllerSkills(snapshot) {
	const visibleSkills = snapshot.skills.slice(0, 4).map((skill) => skill.displayName ?? skill.id);
	const remaining = snapshot.skills.length - visibleSkills.length;
	return remaining > 0 ? `${visibleSkills.join(", ")} +${remaining} more` : visibleSkills.join(", ");
}
function formatAgentControllerCheckedAt(value) {
	const timestamp = Date.parse(value);
	if (!Number.isFinite(timestamp)) return value;
	return new Intl.DateTimeFormat(void 0, {
		dateStyle: "medium",
		timeStyle: "short"
	}).format(timestamp);
}
//#endregion
//#region src/agent-controller-ref.ts
const AGENT_CONTROLLER_REF_SEPARATOR = ":";
const SUPPORTED_AGENT_CONTROLLER_REF_KINDS = [
	"builtin",
	"custom",
	HOSTED_CENTER_AGENT_CONTROLLER_REF$1
];
const AGENT_CONTROLLER_SOURCE_ID_RE = /^[a-zA-Z0-9_.-]+$/;
const normalizeCapabilitySourceId = (input) => {
	if (input.kind === HOSTED_CENTER_AGENT_CONTROLLER_REF$1) {
		if (input.id.trim() !== HOSTED_CENTER_AGENT_CONTROLLER_REF_ID$1) throw new Error("runtime.agent_controller_ref_invalid: hosted Center Agent id must be center-agent");
		return HOSTED_CENTER_AGENT_CONTROLLER_REF_ID$1;
	}
	const prefix = `${input.kind}${AGENT_CONTROLLER_REF_SEPARATOR}`;
	const rawId = input.id.trim();
	const id = rawId.startsWith(prefix) ? rawId.slice(prefix.length).trim() : rawId;
	if (!(id && AGENT_CONTROLLER_SOURCE_ID_RE.test(id))) throw new Error(`runtime.agent_controller_ref_invalid: invalid ${input.kind} capability id`);
	return id;
};
const buildAgentControllerRef = (input) => {
	const id = normalizeCapabilitySourceId(input);
	if (input.kind === HOSTED_CENTER_AGENT_CONTROLLER_REF$1) return HOSTED_CENTER_AGENT_CONTROLLER_REF$1;
	return `${input.kind}${AGENT_CONTROLLER_REF_SEPARATOR}${id}`;
};
const parseAgentControllerRef = (value) => {
	const ref = String(value ?? "").trim();
	if (!ref) return null;
	if (ref === HOSTED_CENTER_AGENT_CONTROLLER_REF$1) return {
		kind: HOSTED_CENTER_AGENT_CONTROLLER_REF$1,
		id: HOSTED_CENTER_AGENT_CONTROLLER_REF_ID$1,
		ref: HOSTED_CENTER_AGENT_CONTROLLER_REF$1
	};
	for (const kind of SUPPORTED_AGENT_CONTROLLER_REF_KINDS) {
		if (kind === HOSTED_CENTER_AGENT_CONTROLLER_REF$1) continue;
		const prefix = `${kind}${AGENT_CONTROLLER_REF_SEPARATOR}`;
		if (!ref.startsWith(prefix)) continue;
		const id = ref.slice(prefix.length).trim();
		if (!(id && AGENT_CONTROLLER_SOURCE_ID_RE.test(id))) return null;
		return {
			kind,
			id,
			ref: `${kind}${AGENT_CONTROLLER_REF_SEPARATOR}${id}`
		};
	}
	return null;
};
const resolveAgentControllerRefFromAgentTarget = (input) => {
	return buildAgentControllerRef({
		kind: input.targetKind,
		id: input.agentId
	});
};
const runtimeAgentControllerRefMatchesAdapterSnapshot = (input) => {
	const agentControllerRef = parseAgentControllerRef(input.agentControllerRef);
	if (!agentControllerRef) return false;
	if (agentControllerRef.kind === HOSTED_CENTER_AGENT_CONTROLLER_REF$1) return false;
	return String(input.adapter.adapterId ?? "").trim() === agentControllerRef.id;
};
const hasRuntimeAdapterCapabilityForRef = (input) => {
	return (input.runtimeAdapters ?? []).some((adapter) => runtimeAgentControllerRefMatchesAdapterSnapshot({
		adapter,
		agentControllerRef: input.agentControllerRef
	}));
};
const DEFAULT_AGENT_CONTROLLER_PERMISSION_MODE = "claude-code:default";
const CLAUDE_CODE_AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS = [
	{
		description: "Default Claude Code permission mode. CLI: claude --permission-mode default --allowedTools \"Bash(wakeloop)\" \"Bash(wakeloop *)\".",
		label: "Ask permissions",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "claude-code:default"
	},
	{
		description: "Accept file edits automatically. CLI: claude --permission-mode acceptEdits --allowedTools \"Bash(wakeloop)\" \"Bash(wakeloop *)\".",
		label: "Accept edits",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "claude-code:acceptEdits"
	},
	{
		description: "Plan before making changes. CLI: claude --permission-mode plan --allowedTools \"Bash(wakeloop)\" \"Bash(wakeloop *)\".",
		label: "Plan mode",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "claude-code:plan"
	},
	{
		description: "Use Claude Code auto mode. CLI: claude --permission-mode auto --allowedTools \"Bash(wakeloop)\" \"Bash(wakeloop *)\".",
		label: "Auto mode",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "claude-code:auto"
	},
	{
		description: "Automatically reject permission requests not already allowed. CLI: claude --permission-mode dontAsk --allowedTools \"Bash(wakeloop)\" \"Bash(wakeloop *)\".",
		label: "Don't ask",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "claude-code:dontAsk"
	},
	{
		description: "Bypass Claude Code permission prompts. CLI: claude --permission-mode bypassPermissions.",
		label: "Bypass permissions",
		requiresExplicitConfirmation: true,
		tone: "warning",
		value: "claude-code:bypassPermissions"
	}
];
const CODEX_AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS = [
	{
		description: "Always ask to edit external files and use the internet. CLI: codex -s workspace-write -a untrusted.",
		label: "Ask for approval",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "codex:askForApproval"
	},
	{
		description: "Only ask for actions detected as potentially unsafe. CLI: codex -s workspace-write -a on-request -c approvals_reviewer=\"auto_review\".",
		label: "Approve for me",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "codex:approveForMe"
	},
	{
		description: "Unrestricted access to the internet and any file on your computer. CLI: codex --dangerously-bypass-approvals-and-sandbox.",
		label: "Full access",
		requiresExplicitConfirmation: true,
		tone: "warning",
		value: "codex:fullAccess"
	},
	{
		description: "Uses permissions defined in config.toml. CLI: codex without WakeLoop sandbox or approval overrides.",
		label: "Custom (config.toml)",
		requiresExplicitConfirmation: false,
		tone: "neutral",
		value: "codex:customConfig"
	}
];
const AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS_BY_CONTROLLER_ID = {
	"claude-code": CLAUDE_CODE_AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS,
	codex: CODEX_AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS
};
const AGENT_CONTROLLER_PERMISSION_MODE_FIELD_LABEL_BY_CONTROLLER_ID = {
	"claude-code": "Claude Code permissions mode",
	codex: "Codex permissions mode"
};
function resolveAgentControllerPermissionMode(value) {
	const parsed = agentControllerPermissionModeSchema.safeParse(value);
	return parsed.success ? parsed.data : DEFAULT_AGENT_CONTROLLER_PERMISSION_MODE;
}
function getAgentControllerPermissionModeDisplayMetadata(value, input) {
	return {
		...resolveAgentControllerPermissionModeOption(input ? resolveAgentControllerPermissionModeForController(value, input) : resolveAgentControllerPermissionMode(value), input),
		fieldLabel: getAgentControllerPermissionModeFieldLabel(input)
	};
}
function getAgentControllerPermissionModeFieldLabel(input) {
	const parsedRef = parseAgentControllerRef(input?.agentControllerRef);
	if (parsedRef?.kind === "builtin" && isAgentControllerPermissionModeControllerId(parsedRef.id)) return AGENT_CONTROLLER_PERMISSION_MODE_FIELD_LABEL_BY_CONTROLLER_ID[parsedRef.id];
	return "Agent Controller permissions mode";
}
function resolveAgentControllerPermissionModeForController(value, input) {
	const options = resolveControllerOptions(input?.agentControllerRef);
	const controllerDefault = options?.[0]?.value ?? "claude-code:default";
	const parsed = agentControllerPermissionModeSchema.safeParse(value);
	if (!parsed.success) return controllerDefault;
	if (!options) return input?.agentControllerRef ? controllerDefault : parsed.data;
	return options.some((option) => option.value === parsed.data) ? parsed.data : controllerDefault;
}
function resolveNativeAgentControllerPermissionModeForController(value, input) {
	const options = resolveControllerOptions(input.agentControllerRef);
	if (!options) return;
	const parsed = agentControllerPermissionModeSchema.safeParse(value);
	if (!parsed.success) return value === null || value === void 0 ? options[0]?.value : void 0;
	return options.some((option) => option.value === parsed.data) ? parsed.data : void 0;
}
function listAgentControllerPermissionModeOptions(input) {
	const controllerOptions = resolveControllerOptions(input?.agentControllerRef);
	if (controllerOptions) return controllerOptions.map((option) => ({ ...option }));
	if (input) return [];
	return [...CLAUDE_CODE_AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS, ...CODEX_AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS].map((option) => ({ ...option }));
}
function agentControllerPermissionModeRequiresExplicitConfirmation(value) {
	return getAgentControllerPermissionModeDisplayMetadata(value).requiresExplicitConfirmation;
}
function resolveAgentControllerPermissionModeOption(policy, input) {
	return (resolveControllerOptions(input?.agentControllerRef) ?? listAgentControllerPermissionModeOptions()).find((option) => option.value === policy) ?? CLAUDE_CODE_AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS[0];
}
function resolveControllerOptions(agentControllerRef) {
	const parsedRef = parseAgentControllerRef(agentControllerRef);
	if (parsedRef?.kind !== "builtin") return null;
	if (!isAgentControllerPermissionModeControllerId(parsedRef.id)) return null;
	return AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS_BY_CONTROLLER_ID[parsedRef.id] ?? null;
}
function isAgentControllerPermissionModeControllerId(value) {
	return Object.hasOwn(AGENT_CONTROLLER_PERMISSION_MODE_OPTIONS_BY_CONTROLLER_ID, value);
}
//#endregion
//#region src/agent-execution-reason-copy.ts
const DUPLICATE_REPLY_CONVERSATION_REASON = "This agent shares a reply conversation with another Agent Profile.";
const DUPLICATE_REPLY_CONVERSATION_TOOLTIP = "Duplicate reply conversation";
const DUPLICATE_REPLY_CONVERSATION_CONTEXT = "Duplicate reply conversation";
//#endregion
//#region src/agent-execution-status.ts
function resolveAgentExecutionStatus(input) {
	const profile = resolveProfileFact(input.profile);
	const binding = resolveBindingFact({
		agentProfileId: input.agentProfileId,
		binding: input.binding
	});
	const claim = resolveClaimFact(input.claimDiagnostics);
	const providerAccount = resolveProviderAccountFact(input.providerAccount);
	const providerConversation = resolveProviderConversationFact(input.providerConversation);
	const readinessImpact = resolveReadinessImpactFact(input.readinessImpact);
	const route = resolveRouteFact(input.route);
	const localService = resolveLocalServiceFact({
		localService: input.localService,
		routeOnline: route.online
	});
	const service = resolveServiceFact(input.service);
	const runtime = resolveRuntimeFact({
		runtimeReadiness: input.runtimeReadiness,
		service
	});
	const reasonCodes = uniqueReasonCodes$2([
		...profile.reasonCodes,
		...binding.reasonCodes,
		...localService.reasonCodes,
		...service.reasonCodes,
		...runtime.reasonCodes,
		...claim.reasonCodes,
		...providerAccount.reasonCodes,
		...providerConversation.reasonCodes,
		...readinessImpact.reasonCodes,
		...route.reasonCodes
	]);
	const displayState = resolveDisplayState({
		bindingBlocked: binding.source === "blocked",
		bindingRoutable: binding.routable,
		claimBlocked: claim.executionBlocked,
		localServiceReachable: localService.reachable,
		profile,
		providerAccountBlocked: providerAccount.blocked,
		readinessImpactBlocked: readinessImpact.blocked,
		routeOnline: route.online,
		runtimeReady: runtime.ready,
		serviceExecutionCompatible: service.executionCompatible
	});
	return {
		agentProfileId: input.agentProfileId,
		displayState,
		facts: {
			profile,
			binding,
			claim,
			providerAccount,
			providerConversation,
			readinessImpact,
			localService,
			service,
			route,
			runtime
		},
		primaryAction: resolvePrimaryAction({
			binding,
			claim,
			displayState,
			localService,
			profile,
			providerAccount,
			providerConversation,
			readinessImpact,
			route,
			runtime,
			service
		}),
		reasonCodes
	};
}
function resolveProviderConversationFact(providerConversation) {
	if (!providerConversation) return {
		agentControllerConversationId: null,
		errorCode: null,
		errorMessage: null,
		errorType: null,
		expiresAtMs: null,
		method: null,
		observedAtMs: null,
		provider: null,
		ready: true,
		reasonCodes: [],
		required: false,
		source: "not_required",
		status: "not_required"
	};
	return {
		agentControllerConversationId: providerConversation.agentControllerConversationId,
		errorCode: providerConversation.errorCode ?? null,
		errorMessage: providerConversation.errorMessage ?? null,
		errorType: providerConversation.errorType ?? null,
		expiresAtMs: providerConversation.expiresAtMs,
		method: providerConversation.method,
		observedAtMs: providerConversation.observedAtMs,
		provider: providerConversation.provider,
		ready: providerConversation.ready,
		reasonCodes: providerConversation.reasonCodes,
		required: providerConversation.required,
		source: providerConversation.source,
		status: providerConversation.status
	};
}
function resolveProviderAccountFact(providerAccount) {
	if (!providerAccount) return {
		blocked: false,
		expiresAtMs: null,
		observedAtMs: null,
		provider: null,
		reasonCodes: [],
		source: "not_checked"
	};
	return {
		blocked: providerAccount.blocked,
		expiresAtMs: providerAccount.expiresAtMs,
		observedAtMs: providerAccount.observedAtMs,
		provider: providerAccount.provider,
		reasonCodes: providerAccount.reasonCodes,
		source: providerAccount.source
	};
}
function resolveReadinessImpactFact(readinessImpact) {
	if (!readinessImpact) return {
		blocked: false,
		expiresAtMs: null,
		impactKind: null,
		observedAtMs: null,
		reasonCodes: [],
		source: "not_checked"
	};
	return {
		blocked: readinessImpact.blocked,
		expiresAtMs: readinessImpact.expiresAtMs,
		impactKind: readinessImpact.impactKind,
		observedAtMs: readinessImpact.observedAtMs,
		reasonCodes: readinessImpact.reasonCodes,
		source: readinessImpact.source
	};
}
function resolveProfileFact(profile) {
	if (profile) return {
		active: profile.active,
		reasonCodes: profile.reasonCodes,
		source: profile.source
	};
	return {
		active: true,
		reasonCodes: [],
		source: "not_checked"
	};
}
function resolveLocalServiceFact(input) {
	if (input.localService) return {
		reachable: input.localService.reachable,
		refreshable: input.localService.refreshable,
		reasonCodes: input.localService.reasonCodes,
		source: input.localService.source
	};
	if (input.routeOnline) return {
		reachable: true,
		refreshable: true,
		reasonCodes: [],
		source: "runtime_transport"
	};
	return {
		reachable: false,
		refreshable: false,
		reasonCodes: [],
		source: "not_checked"
	};
}
function resolveServiceFact(service) {
	if (service) return {
		executionCompatible: service.executionCompatible,
		expectedEpoch: service.expectedEpoch,
		observedEpoch: service.observedEpoch,
		reasonCodes: service.reasonCodes,
		source: service.source
	};
	return {
		executionCompatible: true,
		expectedEpoch: "",
		observedEpoch: null,
		reasonCodes: [],
		source: "not_checked"
	};
}
function resolveBindingFact(input) {
	const { agentProfileId, binding } = input;
	if (!binding) return {
		reasonCodes: ["agent_profile_binding.missing"],
		routable: false,
		source: "missing"
	};
	if (isNotCheckedBindingEvidence(binding)) return {
		reasonCodes: binding.reasonCodes ?? [],
		routable: false,
		source: "not_checked"
	};
	if (isUnavailableBindingEvidence(binding)) return {
		reasonCodes: binding.reasonCodes,
		routable: false,
		source: "unavailable"
	};
	if (binding.agentProfileId !== agentProfileId) return {
		reasonCodes: ["agent_execution.binding_agent_profile_mismatch"],
		routable: false,
		source: "blocked"
	};
	if (binding.authoritySource === "binding_truth" && binding.bindingState === "enabled_primary" && binding.dispatchEligibility.routable) return {
		reasonCodes: binding.reasonCodes,
		routable: true,
		source: "binding_truth"
	};
	return {
		reasonCodes: binding.reasonCodes,
		routable: false,
		source: "blocked"
	};
}
function isNotCheckedBindingEvidence(binding) {
	return "status" in binding && binding.status === "not_checked";
}
function isUnavailableBindingEvidence(binding) {
	return "status" in binding && binding.status === "unavailable";
}
function resolveRuntimeFact(input) {
	const { runtimeReadiness, service } = input;
	if (!runtimeReadiness) return {
		ready: false,
		reasonCodes: ["runtime.projection_missing"],
		source: "missing"
	};
	return {
		ready: runtimeReadiness.ready || service.executionCompatible && hasOnlyAdvisoryServiceDriftReason(runtimeReadiness.reasonCodes),
		reasonCodes: runtimeReadiness.reasonCodes,
		source: "prepare_readiness"
	};
}
function resolveClaimFact(claimDiagnostics) {
	if (!claimDiagnostics || claimDiagnostics.status === "not_checked") return {
		executionBlocked: false,
		grantsClaimSuccess: false,
		reasonCodes: [],
		source: "not_checked"
	};
	const reasonCodes = claimDiagnostics.reasonCodes ?? [];
	if (claimDiagnostics.status === "accepted") return {
		executionBlocked: false,
		grantsClaimSuccess: false,
		reasonCodes,
		source: "claim_diagnostics"
	};
	return {
		executionBlocked: true,
		grantsClaimSuccess: false,
		reasonCodes,
		source: "claim_diagnostics"
	};
}
function resolveRouteFact(route) {
	if (!route) return {
		deviceId: null,
		lane: null,
		observedAt: null,
		online: false,
		reasonCodes: ["agent_execution.route_missing"],
		source: "missing"
	};
	if (route.status === "online") return {
		deviceId: route.deviceId?.trim() || null,
		lane: route.lane?.trim() || null,
		observedAt: route.observedAt?.trim() || null,
		online: true,
		reasonCodes: route.reasonCodes ?? [],
		source: "daemon_route"
	};
	return {
		deviceId: route.deviceId?.trim() || null,
		lane: route.lane?.trim() || null,
		observedAt: route.observedAt?.trim() || null,
		online: false,
		reasonCodes: route.reasonCodes,
		source: "daemon_route"
	};
}
function resolveDisplayState(input) {
	if (input.profile.source === "unavailable") return "unknown";
	if (!input.profile.active) return "blocked";
	if (input.bindingBlocked) return "blocked";
	if (input.bindingRoutable && !input.serviceExecutionCompatible) return "blocked";
	if (input.claimBlocked) return "blocked";
	if (input.providerAccountBlocked) return "blocked";
	if (input.readinessImpactBlocked) return "blocked";
	if (input.bindingRoutable && input.localServiceReachable && input.serviceExecutionCompatible && input.runtimeReady && input.routeOnline) return "ready";
	return "not_ready";
}
function resolvePrimaryAction(input) {
	if (input.displayState === "ready") return "none";
	if (!input.profile.active || input.profile.source === "unavailable") return "check_status";
	if (!input.binding.routable) return input.binding.source === "unavailable" || input.binding.source === "not_checked" ? "check_status" : "reconnect_binding";
	const serviceAction = resolveServicePrimaryAction(input);
	if (serviceAction) return serviceAction;
	return resolveRuntimeRoutePrimaryAction(input);
}
function resolveServicePrimaryAction(input) {
	if (!input.service.executionCompatible || hasServiceUpdateRequiredReason(input.service.reasonCodes)) return "update_service";
	if (hasServiceUpdateRequiredReason(input.runtime.reasonCodes)) return "update_service";
	if (hasBindingReconnectReason(input.localService.reasonCodes) || input.claim.executionBlocked && hasBindingReconnectReason(input.claim.reasonCodes)) return "reconnect_binding";
	if (hasServiceConnectionReason(input.localService.reasonCodes) || hasServiceConnectionReason(input.service.reasonCodes) || input.claim.executionBlocked && hasServiceConnectionReason(input.claim.reasonCodes)) return "connect_service";
	return null;
}
function resolveRuntimeRoutePrimaryAction(input) {
	if (!(input.localService.reachable || input.route.online)) return "run_prepare";
	if (!input.runtime.ready) return resolveRuntimeNotReadyPrimaryAction({
		localService: input.localService,
		runtime: input.runtime
	});
	if (!input.route.online) return input.localService.refreshable ? "check_status" : "run_prepare";
	if (input.claim.executionBlocked) return resolveBlockedClaimPrimaryAction({
		claim: input.claim,
		localService: input.localService,
		runtime: input.runtime,
		service: input.service
	});
	if (input.providerAccount.blocked) return "fix_provider_account";
	if (input.readinessImpact.blocked) return "run_prepare";
	return "check_status";
}
function resolveBlockedClaimPrimaryAction(input) {
	const claimAndRuntimeReasonCodes = [
		...input.claim.reasonCodes,
		...input.runtime.reasonCodes,
		...input.service.reasonCodes
	];
	if (hasServiceUpdateRequiredReason(claimAndRuntimeReasonCodes)) return "update_service";
	if (hasServiceConnectionReason(input.claim.reasonCodes)) return "connect_service";
	if (hasRuntimeAgentControllerReportRefreshReason(claimAndRuntimeReasonCodes) && input.localService.refreshable) return "check_status";
	if (hasBindingReconnectReason(input.claim.reasonCodes)) return "reconnect_binding";
	if (hasSetupRepairReason(input.claim.reasonCodes)) return "run_prepare";
	return "run_prepare";
}
function hasBindingReconnectReason(reasonCodes) {
	return reasonCodes.some((reasonCode) => {
		return reasonCode === "claim_eligibility.binding_capability_mismatch" || reasonCode === "claim_eligibility.binding_owner_mismatch" || reasonCode === "claim_eligibility.binding_runtime_mismatch" || reasonCode === "claim_eligibility.owner_mismatch" || reasonCode === "claim_eligibility.wakeloop_service_runtime_id_mismatch" || reasonCode === "local_service.identity_mismatch";
	});
}
function hasServiceConnectionReason(reasonCodes) {
	return reasonCodes.some((reasonCode) => reasonCode === "local_service.not_connected_for_account" || reasonCode === "service.current_runtime_session_missing" || reasonCode === "daemon.session.not_found");
}
function resolveRuntimeNotReadyPrimaryAction(input) {
	if (hasRuntimeAgentControllerReportRefreshReason(input.runtime.reasonCodes) && input.localService.refreshable) return "check_status";
	return "run_prepare";
}
function hasRuntimeAgentControllerReportRefreshReason(reasonCodes) {
	return reasonCodes.some((reasonCode) => reasonCode === "runtime.agent_controller_report_stale" || reasonCode === "runtime.agent_controller_report_missing" || reasonCode === "runtime.projection_missing" || reasonCode === "claim_eligibility.agent_controller_report_missing" || reasonCode === "claim_eligibility.agent_controller_report_stale");
}
function isAgentExecutionRouteStatusUpdatingReasonCode(reasonCode) {
	return reasonCode === "agent_execution.route_missing" || reasonCode === "agent_execution.route_offline" || reasonCode === "agent_execution.route_unavailable" || reasonCode === "route.not_registered";
}
function isAgentExecutionLocalReadinessRefreshingReasonCode(reasonCode) {
	return hasRuntimeAgentControllerReportRefreshReason([reasonCode]);
}
function isAgentExecutionSelfHealingReasonCode(reasonCode) {
	return isAgentExecutionRouteStatusUpdatingReasonCode(reasonCode) || isAgentExecutionLocalReadinessRefreshingReasonCode(reasonCode);
}
function isAgentExecutionSelfHealingReadiness(readiness) {
	const reasonCodes = getAgentExecutionReadinessReasonCodes(readiness);
	if (hasServiceUpdateRequiredReason(reasonCodes)) return false;
	return readiness.primaryAction === "check_status" && readiness.facts.localService.refreshable && reasonCodes.some(isAgentExecutionSelfHealingReasonCode);
}
function isAgentExecutionReadyOrConnecting(readiness) {
	return readiness.displayState === "ready";
}
function getAgentExecutionReadinessReasonCodes(readiness) {
	return [
		...readiness.reasonCodes,
		...readiness.facts.profile.reasonCodes,
		...readiness.facts.binding.reasonCodes,
		...readiness.facts.claim.reasonCodes,
		...readiness.facts.providerAccount.reasonCodes,
		...readiness.facts.providerConversation.reasonCodes,
		...readiness.facts.readinessImpact.reasonCodes,
		...readiness.facts.localService.reasonCodes,
		...readiness.facts.service.reasonCodes,
		...readiness.facts.route.reasonCodes,
		...readiness.facts.runtime.reasonCodes
	];
}
function hasSetupRepairReason(reasonCodes) {
	return reasonCodes.some((reasonCode) => {
		if (reasonCode === "claim_eligibility.binding_capability_mismatch" || reasonCode === "claim_eligibility.binding_owner_mismatch" || reasonCode === "claim_eligibility.binding_runtime_mismatch" || reasonCode === "claim_eligibility.capability_missing" || reasonCode === "claim_eligibility.capability_unavailable" || reasonCode === "claim_eligibility.owner_mismatch" || reasonCode === "claim_eligibility.agent_controller_report_disabled" || reasonCode === "claim_eligibility.agent_controller_report_missing" || reasonCode === "claim_eligibility.agent_controller_report_stale" || reasonCode === "claim_eligibility.agent_controller_report_unavailable" || reasonCode === "claim_eligibility.agent_controller_report_unknown" || reasonCode === "claim_eligibility.runtime_conflict" || reasonCode === "claim_eligibility.wakeloop_service_runtime_id_mismatch" || reasonCode === "claim_eligibility.wakeloop_service_runtime_id_missing" || reasonCode === "claim_eligibility.wakeloop_service_runtime_id_not_opaque" || reasonCode === "claim_eligibility.wakeloop_service_runtime_identity_invalid" || reasonCode === "claim_eligibility.runtime_missing" || reasonCode === "claim_eligibility.runtime_unavailable" || reasonCode === "dispatch.claim_eligibility.binding.conflict" || reasonCode === "dispatch.claim_eligibility.binding.disabled_historical" || reasonCode === "dispatch.claim_eligibility.binding.owner_mismatch") return true;
		return reasonCode.startsWith("claim_eligibility.agent_controller_report_") || reasonCode.startsWith("runtime.agent_controller_report");
	});
}
function hasServiceUpdateRequiredReason(reasonCodes) {
	return reasonCodes.some((reasonCode) => reasonCode === "service.update_required" || reasonCode === "background_helper.update_required" || reasonCode === "daemon.runtime_contract.mismatch");
}
function hasOnlyAdvisoryServiceDriftReason(reasonCodes) {
	return reasonCodes.length > 0 && reasonCodes.every((reasonCode) => reasonCode === "service.drifted");
}
function uniqueReasonCodes$2(reasonCodes) {
	return [...new Set(reasonCodes.filter((reasonCode) => reasonCode.trim()))];
}
//#endregion
//#region src/agent-execution-status-display.ts
function createAgentExecutionStatusDisplayRows(projection) {
	return [
		{
			label: "Reply status",
			value: formatAgentExecutionStatus(projection)
		},
		{
			label: "Connection",
			value: formatBindingEvidence(projection)
		},
		{
			label: "Local agent capability",
			value: formatRuntimeEvidence(projection)
		},
		{
			label: "WakeLoop Service",
			value: formatLocalServiceEvidence(projection)
		},
		{
			label: "Reply route",
			value: formatRouteEvidence(projection)
		},
		{
			label: "Wake",
			value: formatClaimEvidence(projection)
		}
	];
}
function formatAgentExecutionStatus(projection) {
	switch (projection.displayState) {
		case "ready": return "Available for Wake · This agent can reply from this computer.";
		case "blocked": return `Blocked · ${formatBlockingReason(projection)}`;
		case "not_ready": return `Unavailable · ${formatNotReadyReason(projection)}`;
		case "unknown": return "Unknown";
		default: return "Unknown";
	}
}
function formatBlockingReason(projection) {
	if (!projection.facts.profile.active) return formatPrimaryReason(projection.facts.profile.reasonCodes);
	if (projection.facts.binding.source === "blocked") return formatPrimaryReason(projection.facts.binding.reasonCodes);
	if (!projection.facts.service.executionCompatible) return formatPrimaryReason(projection.facts.service.reasonCodes);
	if (projection.facts.claim.executionBlocked) return formatPrimaryReason(projection.facts.claim.reasonCodes);
	if (projection.facts.providerAccount.blocked) return formatPrimaryReason(projection.facts.providerAccount.reasonCodes);
	if (projection.facts.readinessImpact.blocked) return formatPrimaryReason(projection.facts.readinessImpact.reasonCodes);
	return formatPrimaryReason(projection.reasonCodes);
}
function formatBindingEvidence(projection) {
	if (projection.facts.binding.routable) return "Local agent connection is set.";
	if (projection.facts.binding.source === "missing") return "Choose a local agent for this profile.";
	if (projection.facts.binding.source === "not_checked") return "Local agent connection has not been checked for this profile.";
	if (projection.facts.binding.source === "unavailable") return formatPrimaryReason(projection.facts.binding.reasonCodes);
	return formatPrimaryReason(projection.facts.binding.reasonCodes);
}
function formatLocalServiceEvidence(projection) {
	if (!projection.facts.service.executionCompatible) return formatPrimaryReason(projection.facts.service.reasonCodes);
	if (projection.facts.localService.reachable) return projection.facts.localService.refreshable ? "WakeLoop Service can check this agent again on this computer." : "WakeLoop Service is connected for this account.";
	if (projection.facts.localService.source === "not_checked") return "WakeLoop Service has not been checked for this agent yet.";
	return formatPrimaryReason(projection.facts.localService.reasonCodes);
}
function formatRuntimeEvidence(projection) {
	if (projection.facts.runtime.ready) return "Local agent capability is current.";
	if (projection.facts.runtime.source === "missing") return "WakeLoop needs a current setup check from this computer.";
	if (projection.facts.runtime.reasonCodes.includes("prepare_readiness.read_failed")) return formatPrimaryReason(projection.facts.runtime.reasonCodes);
	return formatPrimaryReason(projection.facts.runtime.reasonCodes);
}
function formatRouteEvidence(projection) {
	if (!projection.facts.service.executionCompatible) return "Update WakeLoop Service before checking this agent's local reply route.";
	if (projection.facts.route.online) return "WakeLoop Service is online for replies.";
	if (projection.facts.route.source === "missing") return REPLY_ROUTE_RECONNECTING_REASON;
	return formatPrimaryReason(projection.facts.route.reasonCodes);
}
function formatClaimEvidence(projection) {
	if (projection.facts.claim.source === "not_checked") return "Wake has not been checked yet.";
	if (projection.facts.claim.executionBlocked) return formatPrimaryReason(projection.facts.claim.reasonCodes);
	if (projection.displayState === "ready") return "Wake is available from this computer.";
	if (!projection.facts.runtime.ready) return "Wake is waiting for this computer to check local agent support again.";
	if (!projection.facts.binding.routable) return "Wake needs a local agent connection first.";
	if (!projection.facts.localService.reachable) return "Wake needs WakeLoop Service connected for this account.";
	if (!projection.facts.service.executionCompatible) return "Wake needs WakeLoop Service updated before this agent can reply.";
	if (!projection.facts.route.online) return "Wake is waiting for this agent's local reply route to reconnect.";
	return "Wake is waiting for setup to finish.";
}
function formatPrimaryReason(reasonCodes) {
	const reasonCode = selectPrimaryReasonCode(reasonCodes);
	if (!reasonCode) return "no supporting evidence";
	return REASON_TEXT[reasonCode] ?? formatReasonCodeFamily(reasonCode);
}
function selectPrimaryReasonCode(reasonCodes) {
	return reasonCodes.find((reasonCode) => !PRIMARY_REASON_UMBRELLA_CODES.has(reasonCode)) ?? reasonCodes[0] ?? null;
}
function formatReasonCodeFamily(reasonCode) {
	if (reasonCode.startsWith("agent_profile.")) return "WakeLoop could not confirm this agent profile.";
	if (reasonCode.startsWith("service.") || reasonCode.startsWith("background_helper.") || reasonCode.startsWith("daemon.runtime_contract.")) return "WakeLoop Service is not connected on this computer.";
	if (reasonCode.startsWith("claim_eligibility.") || reasonCode.startsWith("dispatch.claim_eligibility.")) return "Wake cannot use this local connection for this agent yet.";
	if (reasonCode.startsWith("runtime.agent_controller_report_") || reasonCode.startsWith("runtime.agent_controller_report.")) return "This computer has not reported current local agent support yet.";
	if (reasonCode.startsWith("provider_conversation.")) return "WakeLoop needs a current provider conversation check for this agent.";
	if (reasonCode.startsWith("runtime.")) return "This computer needs a fresh check for this agent.";
	if (reasonCode.startsWith("agent_profile_binding.")) return "This agent's local connection needs setup.";
	if (reasonCode.startsWith("local_service.")) return "WakeLoop Service is not connected for this account.";
	if (reasonCode.startsWith("agent_execution.route_")) return REPLY_ROUTE_RECONNECTING_REASON;
	return "WakeLoop needs a more current setup check from this computer.";
}
function formatNotReadyReason(projection) {
	if (!projection.facts.profile.active || projection.facts.profile.source === "unavailable") return formatPrimaryReason(projection.facts.profile.reasonCodes);
	if (!projection.facts.binding.routable) return formatPrimaryReason(projection.facts.binding.reasonCodes);
	if (!projection.facts.localService.reachable) return formatPrimaryReason(projection.facts.localService.reasonCodes);
	if (!projection.facts.service.executionCompatible) return formatPrimaryReason(projection.facts.service.reasonCodes);
	if (!projection.facts.runtime.ready) return formatPrimaryReason(projection.facts.runtime.reasonCodes);
	if (projection.facts.providerConversation.required && !projection.facts.providerConversation.ready) return formatPrimaryReason(projection.facts.providerConversation.reasonCodes);
	if (!projection.facts.route.online) return formatPrimaryReason(projection.facts.route.reasonCodes);
	return formatPrimaryReason(projection.reasonCodes);
}
const PRIMARY_REASON_UMBRELLA_CODES = /* @__PURE__ */ new Set([
	"claim_eligibility.eligible",
	"dispatch.claim_eligibility.blocked",
	"dispatch.claim_eligibility.candidate"
]);
const SERVICE_UPDATE_REQUIRED_REASON = "WakeLoop Service needs an update before this agent can reply from this computer.";
const SERVICE_CONTRACT_NOT_REPORTED_REASON = "WakeLoop Service has not reported execution compatibility yet.";
const OLDER_COMPUTER_SETUP_REASON = "This agent is connected to an older computer setup.";
const DIFFERENT_LOCAL_AGENT_SETUP_REASON = "This agent is connected to a different local agent setup.";
const DIFFERENT_ACCOUNT_REASON = "This agent is connected to a different WakeLoop account.";
const CONFLICTING_REPLY_CONNECTION_REASON = "This agent has a conflicting reply connection.";
const WAKE_REQUEST_NOT_ACTIVE_REASON = "This Wake request is no longer active.";
const AGENT_NOT_IN_SPACE_REASON = "This agent is no longer available in this space.";
const LOCAL_AGENT_READINESS_STALE_REASON = "This computer's setup check is out of date.";
const REPLY_ROUTE_RECONNECTING_REASON = "WakeLoop is checking this agent's local reply route.";
const REASON_TEXT = {
	"agent_profile.identity_lookup_failed": "WakeLoop could not confirm this agent profile.",
	"agent_profile.identity_lookup_unavailable": "WakeLoop could not check this agent profile right now.",
	"agent_profile.inactive": "This agent profile is not active.",
	"agent_profile.not_agent": "This profile is not an agent profile.",
	"agent_profile.not_found": "This agent profile no longer exists.",
	"agent_profile.owner_unavailable": "WakeLoop could not confirm this agent profile's owner.",
	"agent_execution.binding_agent_profile_mismatch": "This local connection belongs to a different agent profile.",
	"agent_execution.route_missing": REPLY_ROUTE_RECONNECTING_REASON,
	"agent_execution.route_offline": REPLY_ROUTE_RECONNECTING_REASON,
	"agent_execution.route_unavailable": "WakeLoop could not confirm this agent's local reply route yet.",
	"agent_profile_binding.missing": "This agent is not connected to a local agent yet.",
	"agent_profile_binding.controller_conversation_conflict": DUPLICATE_REPLY_CONVERSATION_REASON,
	"agent_profile_binding.profile_owner_invalid": "WakeLoop could not confirm this agent profile.",
	"agent_profile_binding.profile_owner_unavailable": "WakeLoop could not confirm this agent profile's owner.",
	"agent_profile_binding.read_unavailable": "WakeLoop could not check this agent's local connection.",
	"controller.launch.choice_required": "Choose which local install this agent should use.",
	"controller.launch.needs_repair": "Repair this local agent before it can reply.",
	"controller.client.upgrade_required": "Update this local agent before it can reply.",
	"controller.runtime.missing": "This local agent is not available on this computer.",
	"runtime_coordinator.delivery_expired": "This agent did not receive the last Wake from this computer.",
	"workspace.invalid": "Repair this agent's local workspace on this computer.",
	"workspace.missing": "Set up this agent's local workspace on this computer.",
	"local_service.identity_mismatch": "WakeLoop Service is connected to a different local runtime for this account.",
	"local_service.not_connected_for_account": "WakeLoop Service is not connected for this WakeLoop account on this computer.",
	"local_service.refresh_in_progress": "WakeLoop Service is checking this agent again on this computer.",
	"local_service.unavailable": "WakeLoop could not check WakeLoop Service for this account.",
	"execution_authority.missing": "Wake execution authority is missing.",
	"execution_authority.mismatch": "Wake execution authority does not match this agent.",
	"execution_authority.unavailable": "Wake execution authority is unavailable.",
	"claim_eligibility.binding_capability_mismatch": DIFFERENT_LOCAL_AGENT_SETUP_REASON,
	"claim_eligibility.binding_conflict": CONFLICTING_REPLY_CONNECTION_REASON,
	"claim_eligibility.binding_disabled": "This agent's local connection is turned off.",
	"claim_eligibility.binding_missing": "This agent is not connected to a local agent yet.",
	"claim_eligibility.binding_not_routable": "This agent's local connection is not available for Wake.",
	"claim_eligibility.binding_owner_mismatch": DIFFERENT_ACCOUNT_REASON,
	"claim_eligibility.binding_runtime_mismatch": OLDER_COMPUTER_SETUP_REASON,
	"claim_eligibility.caller_not_allowed": "WakeLoop could not verify permission to run this agent.",
	"claim_eligibility.capability_missing": "This computer does not currently report support for this local agent.",
	"claim_eligibility.capability_unavailable": "This local agent is not available on this computer right now.",
	"claim_eligibility.dispatch_evidence_missing": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.dispatch_evidence_stale": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.dispatch_missing": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.dispatch_terminal": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.lease_conflict": CONFLICTING_REPLY_CONNECTION_REASON,
	"claim_eligibility.non_wake_relation_not_dispatchable": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.not_read_after_caller_authorization_failed": "WakeLoop could not verify permission to run this agent.",
	"claim_eligibility.owner_mismatch": DIFFERENT_ACCOUNT_REASON,
	"claim_eligibility.policy_version_mismatch": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.agent_controller_report_disabled": "This local agent is turned off on this computer.",
	"claim_eligibility.agent_controller_report_missing": "This computer has not reported this local agent capability yet.",
	"claim_eligibility.agent_controller_report_stale": LOCAL_AGENT_READINESS_STALE_REASON,
	"claim_eligibility.agent_controller_report_unavailable": "This local agent is not available on this computer right now.",
	"claim_eligibility.agent_controller_report_unknown": "WakeLoop cannot confirm this local agent capability yet.",
	"claim_eligibility.runtime_conflict": CONFLICTING_REPLY_CONNECTION_REASON,
	"claim_eligibility.wakeloop_service_runtime_id_mismatch": OLDER_COMPUTER_SETUP_REASON,
	"claim_eligibility.wakeloop_service_runtime_id_missing": OLDER_COMPUTER_SETUP_REASON,
	"claim_eligibility.wakeloop_service_runtime_id_not_opaque": OLDER_COMPUTER_SETUP_REASON,
	"claim_eligibility.wakeloop_service_runtime_identity_invalid": OLDER_COMPUTER_SETUP_REASON,
	"claim_eligibility.runtime_missing": "WakeLoop cannot find the local runtime for this agent.",
	"claim_eligibility.runtime_unavailable": "This local runtime is not available right now.",
	"claim_eligibility.space_liveness_missing": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.space_liveness_stale": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.target_no_longer_member": AGENT_NOT_IN_SPACE_REASON,
	"claim_eligibility.target_not_claimable_agent_member": AGENT_NOT_IN_SPACE_REASON,
	"claim_eligibility.wake_no_longer_allowed_by_policy": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"claim_eligibility.wake_not_allowed_at_creation": WAKE_REQUEST_NOT_ACTIVE_REASON,
	"daemon.runtime_contract.missing": SERVICE_CONTRACT_NOT_REPORTED_REASON,
	"daemon.runtime_contract.mismatch": SERVICE_UPDATE_REQUIRED_REASON,
	"dispatch.claim_eligibility.binding.conflict": CONFLICTING_REPLY_CONNECTION_REASON,
	"dispatch.claim_eligibility.binding.disabled_historical": "This agent's previous local connection is no longer active.",
	"dispatch.claim_eligibility.binding.enabled_primary": "This agent's local connection is not available for Wake.",
	"dispatch.claim_eligibility.binding.missing": "This agent is not connected to a local agent yet.",
	"dispatch.claim_eligibility.binding.owner_mismatch": DIFFERENT_ACCOUNT_REASON,
	"prepare_readiness.read_failed": "WakeLoop could not check this computer for local agent support.",
	"background_helper.update_required": SERVICE_UPDATE_REQUIRED_REASON,
	"runtime.agent_controller_ref_invalid": "WakeLoop could not identify this local agent capability.",
	"runtime.agent_controller_report_disabled": "This local agent is turned off on this computer.",
	"runtime.agent_controller_report_missing": "This computer has not reported this local agent capability yet.",
	"runtime.agent_controller_report_stale": LOCAL_AGENT_READINESS_STALE_REASON,
	"runtime.agent_controller_report_unavailable": "This local agent is not available on this computer right now.",
	"runtime.agent_controller_report_unknown": "WakeLoop cannot confirm this local agent capability yet.",
	"runtime.agent_controller_report.disabled": "This local agent is turned off on this computer.",
	"runtime.agent_controller_report.missing": "This computer has not reported this local agent capability yet.",
	"runtime.agent_controller_report.stale": LOCAL_AGENT_READINESS_STALE_REASON,
	"runtime.agent_controller_report.unavailable": "This local agent is not available on this computer right now.",
	"runtime.agent_controller_report.unknown": "WakeLoop cannot confirm this local agent capability yet.",
	"runtime.projection_missing": "WakeLoop needs a current setup check from this computer.",
	"provider_conversation.proof_failed": "WakeLoop could not resume this agent's provider conversation on this computer.",
	"provider_conversation.proof_missing": "WakeLoop is checking this agent's provider conversation on this computer.",
	"provider_conversation.proof_unavailable": "WakeLoop could not check this agent's provider conversation.",
	"service.update_required": SERVICE_UPDATE_REQUIRED_REASON
};
//#endregion
//#region src/runtime-identity.ts
const FORBIDDEN_WAKELOOP_SERVICE_RUNTIME_IDENTITY_SCOPE_KEYS = /* @__PURE__ */ new Set([
	"agentProfileId",
	"humanProfileId",
	"profileId",
	"wakeLoopProfileId",
	"spaceId",
	"startSessionId",
	"webSocketId",
	"pid",
	"callerPrincipalId"
]);
const normalizeRequiredIdentityField = (input) => {
	const value = input.value.trim();
	if (!value) throw new Error(`runtime.identity_invalid: ${input.fieldName} is required`);
	return value;
};
const assertNoForbiddenRuntimeIdentityScope = (input) => {
	for (const key of FORBIDDEN_WAKELOOP_SERVICE_RUNTIME_IDENTITY_SCOPE_KEYS) if (input[key] !== void 0) throw new Error(`runtime.identity_forbidden_scope: ${key} is not part of Runtime identity`);
};
const createRuntimeIdentityKey = (input) => {
	assertNoForbiddenRuntimeIdentityScope(input);
	const ownerKind = input.owner.kind;
	if (ownerKind !== "owner_account" && ownerKind !== "owner_principal") throw new Error("runtime.identity_invalid: owner kind is not supported");
	return {
		owner: {
			kind: ownerKind,
			id: normalizeRequiredIdentityField({
				fieldName: "owner.id",
				value: input.owner.id
			})
		},
		deviceId: normalizeRequiredIdentityField({
			fieldName: "deviceId",
			value: input.deviceId
		}),
		lane: normalizeRequiredIdentityField({
			fieldName: "lane",
			value: input.lane
		})
	};
};
const wakeLoopServiceRuntimeIdentityKeysEqual = (left, right) => {
	return left.owner.kind === right.owner.kind && left.owner.id === right.owner.id && left.deviceId === right.deviceId && left.lane === right.lane;
};
//#endregion
//#region src/runtime-id-contract.ts
const RUNTIME_REGISTRY_AUTHORITY_SOURCE = "runtime_registry_authority";
const normalizeRuntimeId = (wakeLoopServiceRuntimeId) => {
	const value = wakeLoopServiceRuntimeId.trim();
	if (!value) throw new Error("runtime.id_invalid: wakeLoopServiceRuntimeId is required");
	return value;
};
const getClientComputedLookupForms = (lookupInput) => {
	const owner = lookupInput.owner;
	return /* @__PURE__ */ new Set([
		`${owner.kind}:${owner.id}:${lookupInput.deviceId}:${lookupInput.lane}`,
		`${owner.id}:${lookupInput.deviceId}:${lookupInput.lane}`,
		JSON.stringify(lookupInput)
	]);
};
const createRuntimeLookupInput = (input) => {
	return createRuntimeIdentityKey(input);
};
const createAuthorityWakeLoopServiceRuntimeId = (input) => {
	if (input.source !== RUNTIME_REGISTRY_AUTHORITY_SOURCE) throw new Error("runtime.id_source_invalid: wakeLoopServiceRuntimeId must come from Runtime Registry authority");
	const value = normalizeRuntimeId(input.wakeLoopServiceRuntimeId);
	if (input.lookupInput && getClientComputedLookupForms(input.lookupInput).has(value)) throw new Error("runtime.id_not_opaque: wakeLoopServiceRuntimeId must not be computed from Runtime lookup input");
	return {
		value,
		source: RUNTIME_REGISTRY_AUTHORITY_SOURCE
	};
};
const assertAuthoritativeReadyWakeLoopServiceRuntimeId = (input) => {
	if (!input.wakeLoopServiceRuntimeId) throw new Error("runtime.id_missing: authoritative ready requires Runtime Registry wakeLoopServiceRuntimeId");
};
//#endregion
//#region src/agent-profile-binding.ts
const FALLBACK_REASON_CODE_BY_STATE = {
	missing: "agent_profile_binding.missing",
	enabled_primary: "agent_profile_binding.enabled_primary",
	disabled_historical: "agent_profile_binding.disabled_historical",
	conflict: "agent_profile_binding.conflict",
	owner_mismatch: "agent_profile_binding.owner_mismatch"
};
const normalizeRequiredString = (input) => {
	const value = String(input.value ?? "").trim();
	if (!value) throw new Error(`${input.errorCode}: ${input.fieldName} is required`);
	return value;
};
const normalizeOptionalString = (value) => {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : void 0;
};
const uniqueReasonCodes$1 = (codes) => {
	const seen = /* @__PURE__ */ new Set();
	const normalized = [];
	for (const code of codes) {
		const reasonCode = code.trim();
		if (!(reasonCode && !seen.has(reasonCode))) continue;
		seen.add(reasonCode);
		normalized.push(reasonCode);
	}
	return normalized;
};
const resolveReasonCodes = (input) => {
	return uniqueReasonCodes$1(input.reasonCodes && input.reasonCodes.length > 0 ? input.reasonCodes : [FALLBACK_REASON_CODE_BY_STATE[input.bindingState]]);
};
const createAgentProfileBindingExecutionTarget = (input) => {
	const wakeLoopServiceRuntimeId = normalizeRequiredString({
		fieldName: "wakeLoopServiceRuntimeId",
		value: input.wakeLoopServiceRuntimeId,
		errorCode: "agent_profile_binding.agent_controller_report_invalid"
	});
	const agentControllerRef = normalizeRequiredString({
		fieldName: "agentControllerRef",
		value: input.agentControllerRef,
		errorCode: "agent_profile_binding.agent_controller_report_invalid"
	});
	if (!parseAgentControllerRef(agentControllerRef)) throw new Error("agent_profile_binding.agent_controller_report_invalid: agentControllerRef is invalid");
	const agentControllerConversationId = normalizeOptionalString(input.agentControllerConversationId);
	return {
		wakeLoopServiceRuntimeId,
		agentControllerRef,
		...agentControllerConversationId ? { agentControllerConversationId } : {}
	};
};
const createAgentProfileBindingModelSelection = (input) => {
	const modelId = normalizeOptionalString(input?.modelId);
	if (!modelId) return;
	const reasoningLevel = normalizeOptionalString(input?.reasoningLevel);
	return {
		modelId,
		...reasoningLevel ? { reasoningLevel } : {}
	};
};
const createAgentProfileBindingAgentControllerPermissionMode = (input) => resolveAgentControllerPermissionMode(input);
const resolveResolutionSource = (bindingState) => {
	switch (bindingState) {
		case "missing": return "missing_projection";
		case "conflict": return "conflict_evidence";
		case "enabled_primary":
		case "disabled_historical":
		case "owner_mismatch": return "binding_truth";
		default: return assertUnreachable(bindingState);
	}
};
const resolveAuthoritySource = (bindingState) => {
	switch (bindingState) {
		case "enabled_primary":
		case "disabled_historical": return "binding_truth";
		case "missing": return "none";
		case "conflict":
		case "owner_mismatch": return "blocked";
		default: return assertUnreachable(bindingState);
	}
};
const resolveExecutionTargetForProjection = (input) => {
	switch (input.bindingState) {
		case "enabled_primary":
		case "disabled_historical":
		case "owner_mismatch": return createAgentProfileBindingExecutionTarget(input.executionTarget);
		case "missing":
		case "conflict": return null;
		default: return assertUnreachable(input);
	}
};
const resolveModelSelectionForProjection = (input) => {
	switch (input.bindingState) {
		case "enabled_primary":
		case "disabled_historical":
		case "owner_mismatch": return createAgentProfileBindingModelSelection(input.modelSelection);
		case "missing":
		case "conflict": return;
		default: return assertUnreachable(input);
	}
};
const resolveAgentControllerPermissionModeForProjection = (input) => {
	switch (input.bindingState) {
		case "enabled_primary":
		case "disabled_historical":
		case "owner_mismatch": return resolveNativeAgentControllerPermissionModeForController(input.agentControllerPermissionMode, { agentControllerRef: input.executionTarget.agentControllerRef });
		case "missing":
		case "conflict": return;
		default: return assertUnreachable(input);
	}
};
const resolveOpenCodePermissionForProjection = (input) => {
	switch (input.bindingState) {
		case "enabled_primary":
		case "disabled_historical":
		case "owner_mismatch": {
			if (!isOpenCodeAgentControllerRef(input.executionTarget.agentControllerRef)) return;
			if (input.openCodePermission === void 0 || input.openCodePermission === null) return;
			const parsed = openCodePermissionConfigSchema.safeParse(input.openCodePermission);
			if (!parsed.success) throw new Error("agent_profile_binding.projection_invalid: openCodePermission is invalid");
			return parsed.data;
		}
		case "missing":
		case "conflict": return;
		default: return assertUnreachable(input);
	}
};
const resolveBindingId = (input) => {
	if (!("bindingId" in input)) return;
	return input.bindingId?.trim() || void 0;
};
const resolveDispatchEligibility = (input) => {
	const routable = input.bindingState === "enabled_primary";
	return {
		routable,
		status: routable ? "routable" : "not_routable",
		reasonCodes: input.reasonCodes
	};
};
const createDiagnosticPointer = (input) => ({
	agentProfileId: input.agentProfileId,
	bindingId: input.bindingId,
	wakeLoopServiceRuntimeId: input.executionTarget?.wakeLoopServiceRuntimeId,
	agentControllerRef: input.executionTarget?.agentControllerRef,
	agentControllerConversationId: input.executionTarget?.agentControllerConversationId,
	resolutionSource: input.resolutionSource,
	reasonCodes: input.reasonCodes,
	correlationId: input.diagnosticPointer?.correlationId,
	sourceSignalId: input.diagnosticPointer?.sourceSignalId,
	clientMessageId: input.diagnosticPointer?.clientMessageId,
	dispatchId: input.diagnosticPointer?.dispatchId
});
const createDispatchReadableFacts = (input) => {
	if (!input.executionTarget) return null;
	if (input.bindingState === "missing" || input.bindingState === "conflict") return null;
	return {
		agentProfileId: input.agentProfileId,
		bindingId: input.bindingId,
		wakeLoopServiceRuntimeId: input.executionTarget.wakeLoopServiceRuntimeId,
		agentControllerRef: input.executionTarget.agentControllerRef,
		...input.executionTarget.agentControllerConversationId ? { agentControllerConversationId: input.executionTarget.agentControllerConversationId } : {},
		...input.modelSelection ? { modelSelection: input.modelSelection } : {},
		...input.openCodePermission ? { openCodePermission: input.openCodePermission } : {},
		...input.agentControllerPermissionMode ? { agentControllerPermissionMode: input.agentControllerPermissionMode } : {},
		bindingState: input.bindingState,
		resolutionSource: input.resolutionSource,
		reasonCodes: input.reasonCodes,
		routable: input.routable
	};
};
const createAgentProfileBindingDisplayProjection = (input) => {
	const agentProfileId = normalizeRequiredString({
		fieldName: "agentProfileId",
		value: input.agentProfileId,
		errorCode: "agent_profile_binding.projection_invalid"
	});
	const bindingId = resolveBindingId(input);
	const bindingState = input.bindingState;
	const reasonCodes = resolveReasonCodes({
		bindingState,
		reasonCodes: input.reasonCodes
	});
	const executionTarget = resolveExecutionTargetForProjection(input);
	const agentControllerPermissionMode = resolveAgentControllerPermissionModeForProjection(input);
	const modelSelection = resolveModelSelectionForProjection(input);
	const openCodePermission = resolveOpenCodePermissionForProjection(input);
	const resolutionSource = resolveResolutionSource(bindingState);
	const dispatchEligibility = resolveDispatchEligibility({
		bindingState,
		reasonCodes
	});
	const dispatchReadableFacts = createDispatchReadableFacts({
		agentProfileId,
		bindingId,
		bindingState,
		executionTarget,
		agentControllerPermissionMode,
		modelSelection,
		openCodePermission,
		resolutionSource,
		reasonCodes,
		routable: dispatchEligibility.routable
	});
	return {
		agentProfileId,
		bindingId,
		bindingState,
		resolutionSource,
		authoritySource: resolveAuthoritySource(bindingState),
		executionTarget,
		...agentControllerPermissionMode ? { agentControllerPermissionMode } : {},
		...modelSelection ? { modelSelection } : {},
		...openCodePermission ? { openCodePermission } : {},
		dispatchEligibility,
		dispatchReadableFacts,
		reasonCodes,
		diagnosticPointer: createDiagnosticPointer({
			agentProfileId,
			bindingId,
			executionTarget,
			resolutionSource,
			reasonCodes,
			diagnosticPointer: input.diagnosticPointer
		})
	};
};
function isOpenCodeAgentControllerRef(agentControllerRef) {
	const parsed = parseAgentControllerRef(agentControllerRef);
	return parsed?.kind === "builtin" && parsed.id === "opencode";
}
const resolveAgentProfileBindingTruthWriteBoundary = (input) => {
	if (input.writer === "binding_command") return {
		allowed: true,
		reasonCodes: ["agent_profile_binding.writer_allowed"]
	};
	return {
		allowed: false,
		reasonCodes: ["agent_profile_binding.writer_forbidden"]
	};
};
const assertAgentProfileBindingTruthWriterAllowed = (input) => {
	if (resolveAgentProfileBindingTruthWriteBoundary(input).allowed) return;
	throw new Error(`agent_profile_binding.writer_forbidden: ${input.writer} cannot write Binding truth directly`);
};
const isAuthorityWakeLoopServiceRuntimeId = (value) => {
	if (!(value && typeof value === "object")) return false;
	const candidate = value;
	return candidate.source === "runtime_registry_authority" && typeof candidate.value === "string" && candidate.value.trim().length > 0;
};
const resolveEligibilityAuthorityWakeLoopServiceRuntimeId = (input) => {
	if (!(isAuthorityWakeLoopServiceRuntimeId(input.wakeLoopServiceRuntimeId) && input.runtimeLookupInput)) return { ok: false };
	try {
		return {
			ok: true,
			wakeLoopServiceRuntimeId: createAuthorityWakeLoopServiceRuntimeId({
				lookupInput: input.runtimeLookupInput,
				wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId.value,
				source: input.wakeLoopServiceRuntimeId.source
			})
		};
	} catch {
		return { ok: false };
	}
};
const resolveEligibilityAgentControllerReport = (input) => {
	if (!input) return {
		ok: false,
		reasonCode: "agent_profile_binding.agent_controller_report_missing",
		executionTarget: null
	};
	const agentControllerRef = input.agentControllerRef?.trim();
	const wakeLoopServiceRuntimeId = resolveEligibilityAuthorityWakeLoopServiceRuntimeId(input);
	if (!(wakeLoopServiceRuntimeId.ok && agentControllerRef)) return {
		ok: false,
		reasonCode: "agent_profile_binding.agent_controller_report_invalid",
		executionTarget: null
	};
	if (!parseAgentControllerRef(agentControllerRef)) return {
		ok: false,
		reasonCode: "agent_profile_binding.agent_controller_report_invalid",
		executionTarget: null
	};
	return {
		ok: true,
		executionTarget: {
			wakeLoopServiceRuntimeId: wakeLoopServiceRuntimeId.wakeLoopServiceRuntimeId.value,
			agentControllerRef,
			...normalizeOptionalString(input.agentControllerConversationId) ? { agentControllerConversationId: normalizeOptionalString(input.agentControllerConversationId) } : {}
		}
	};
};
const getCommandEligibilityTimestamp = (value, fieldName) => {
	if (!value) return null;
	const timestamp = value instanceof Date ? value.getTime() : Date.parse(value);
	if (Number.isNaN(timestamp)) throw new Error(`agent_profile_binding.agent_controller_report_time_invalid: ${fieldName} is invalid`);
	return timestamp;
};
const resolveAgentControllerReportAvailabilityBlockReason = (input) => {
	if (input.runtimeOwnerMatches === false) return "agent_profile_binding.runtime_owner_mismatch";
	const report = input.runtimeAgentControllerReport;
	if (!report || report.agentControllerRef !== input.executionTarget.agentControllerRef) return "agent_profile_binding.agent_controller_report_missing";
	const now = getCommandEligibilityTimestamp(input.now, "now");
	if (now === null) return "agent_profile_binding.agent_controller_report_stale";
	const expiresAt = getCommandEligibilityTimestamp(report.expiresAt, "expiresAt");
	if (expiresAt !== null && expiresAt <= now) return "agent_profile_binding.agent_controller_report_stale";
	switch (report.status) {
		case "ready": return null;
		case "disabled": return "agent_profile_binding.agent_controller_report_disabled";
		case "unavailable":
		case "unknown": return "agent_profile_binding.agent_controller_report_unavailable";
		default: return assertUnreachable(report.status);
	}
};
const resolveAgentProfileBindingCommandEligibility = (input) => {
	normalizeRequiredString({
		fieldName: "agentProfileId",
		value: input.agentProfileId,
		errorCode: "agent_profile_binding.command_eligibility_invalid"
	});
	if (input.ownerAuthorization.status !== "allowed") return {
		bindable: false,
		routable: false,
		grantsClaimSuccess: false,
		executionTarget: null,
		reasonCodes: [input.ownerAuthorization.reasonCode]
	};
	if (input.profileOwnerValidation.status !== "valid") return {
		bindable: false,
		routable: false,
		grantsClaimSuccess: false,
		executionTarget: null,
		reasonCodes: [input.profileOwnerValidation.reasonCode]
	};
	const executionTarget = resolveEligibilityAgentControllerReport(input.executionTarget);
	if (!executionTarget.ok) return {
		bindable: false,
		routable: false,
		grantsClaimSuccess: false,
		executionTarget: executionTarget.executionTarget,
		reasonCodes: [executionTarget.reasonCode]
	};
	const executionTargetBlockReason = resolveAgentControllerReportAvailabilityBlockReason({
		executionTarget: executionTarget.executionTarget,
		runtimeAgentControllerReport: input.runtimeAgentControllerReport,
		runtimeOwnerMatches: input.runtimeOwnerMatches,
		now: input.now
	});
	if (executionTargetBlockReason) return {
		bindable: false,
		routable: false,
		grantsClaimSuccess: false,
		executionTarget: executionTarget.executionTarget,
		reasonCodes: [executionTargetBlockReason]
	};
	return {
		bindable: true,
		routable: true,
		grantsClaimSuccess: false,
		executionTarget: executionTarget.executionTarget,
		reasonCodes: ["agent_profile_binding.command_eligible"]
	};
};
function assertUnreachable(value) {
	throw new Error(`Unhandled Agent Profile Binding value: ${String(value)}`);
}
//#endregion
//#region src/agent-profile-edit-capabilities.ts
function resolveAgentProfileEditCapabilities(input) {
	const parsedRef = parseAgentControllerRef(input.agentControllerRef);
	const unavailableReason = resolveModelCatalogUnavailableReason({
		hasCatalog: input.modelCatalog !== null,
		hasParsedRef: parsedRef !== null,
		hasController: parsedRef ? Object.hasOwn(input.modelCatalog?.agentControllers ?? {}, parsedRef.id) : false
	});
	const models = ((parsedRef && input.modelCatalog ? input.modelCatalog.agentControllers[parsedRef.id] ?? null : null)?.models ?? []).filter((model) => !model.unavailable).map(cloneModelCatalogModel);
	const modelSelectionUnavailableReason = unavailableReason ?? (models.length === 0 ? "no_models" : void 0);
	const reasoningLevelModels = models.map((model) => {
		const levels = [...model.supportedReasoningLevels];
		return {
			editable: levels.length > 0,
			levels,
			modelId: model.id
		};
	});
	const permissionModeOptions = listAgentControllerPermissionModeOptions({ agentControllerRef: input.agentControllerRef });
	return {
		rename: {
			editable: true,
			field: "profileName",
			target: "profile"
		},
		modelSelection: {
			editable: modelSelectionUnavailableReason === void 0,
			models,
			source: "model_catalog",
			...modelSelectionUnavailableReason ? { unavailableReason: modelSelectionUnavailableReason } : {}
		},
		reasoningLevel: {
			editable: reasoningLevelModels.some((model) => model.editable),
			models: reasoningLevelModels,
			source: "model_catalog",
			...reasoningLevelModels.length === 0 ? { unavailableReason: modelSelectionUnavailableReason ?? "not_supported" } : {}
		},
		permissionMode: {
			editable: permissionModeOptions.length > 0,
			options: permissionModeOptions,
			source: "agent_controller_permission_mode_options",
			...permissionModeOptions.length === 0 ? { unavailableReason: "not_supported" } : {}
		},
		controllerChange: {
			editable: true,
			fieldPatch: false,
			operation: "ensurePrimaryBinding",
			semantics: "rebind"
		}
	};
}
function resolveModelCatalogUnavailableReason(input) {
	if (!input.hasParsedRef) return "invalid_agent_controller_ref";
	if (!input.hasCatalog) return "model_catalog_unavailable";
	if (!input.hasController) return "agent_controller_not_found";
}
function cloneModelCatalogModel(model) {
	return {
		...model,
		supportedReasoningLevels: [...model.supportedReasoningLevels]
	};
}
//#endregion
//#region src/agent-wake-availability.ts
const serviceNotConnectedReasonCodes = [
	"local_service.not_connected_for_account",
	"local_service.unavailable",
	"service.not_running",
	"service.current_runtime_session_missing",
	"daemon.session.not_found"
];
const routeOfflineReasonCodes = ["agent_execution.route_offline", "route.offline"];
function resolveAgentWakeAvailability(input) {
	if (hasReasonCode(input.readiness, "agent_profile.not_found")) return createAvailability(input.readiness, {
		badgeLabel: "Removed",
		category: "unavailable",
		isRefreshing: false,
		label: "Agent profile deleted",
		phase: "unavailable",
		reasonCode: "agent_profile.not_found",
		status: "unavailable_for_wake",
		summary: "This agent profile no longer exists.",
		tooltip: "Agent profile deleted"
	});
	if (requiresServiceUpdate(input.readiness)) return createAvailability(input.readiness, {
		badgeLabel: "Update",
		category: "unavailable",
		isRefreshing: false,
		label: "Update WakeLoop Service",
		phase: "service_update_required",
		reasonCode: pickReasonCode(input.readiness, ["service.update_required", "background_helper.update_required"]),
		status: "unavailable_for_wake",
		summary: "Update WakeLoop Service before this agent can reply.",
		tooltip: "Update WakeLoop Service"
	});
	if (isAgentWakeAvailable(input)) return createAvailableWakeAvailability();
	if (input.wakeRouteStatus.status === "unavailable" && input.readiness.primaryAction === "connect_service") return createWakeRouteUnavailableAvailability(input.readiness);
	if (hasKnownPublicUnavailableReadiness(input.readiness)) return createUnavailableWakeAvailability(input.readiness);
	const routeRegistrationAvailability = createWakeRouteRegistrationAvailability(input);
	if (routeRegistrationAvailability) return routeRegistrationAvailability;
	if (isCheckingAgentWakeAvailability(input)) return createCheckingWakeAvailability(input);
	if (input.readiness.primaryAction === "connect_service") return createUnavailableWakeAvailability(input.readiness);
	if (hasKnownPublicOfflineReadiness(input)) {
		if (isRefreshableRouteGap(input)) return createCheckingAgentConnectionAvailability({
			reasonCode: pickRouteGapReasonCode(input.readiness),
			readiness: input.readiness
		});
		return createOfflineWakeAvailability({ readiness: input.readiness });
	}
	return createUnavailableWakeAvailability(input.readiness);
}
function resolveHostedAgentWakeAvailability(input) {
	const { readiness } = input;
	const reasonCode = pickHostedAgentReasonCode(readiness);
	if (hasReasonCode(readiness, "agent_profile.not_found")) return {
		action: "none",
		badgeLabel: "Removed",
		category: "unavailable",
		isRefreshing: false,
		label: "Agent profile deleted",
		phase: "unavailable",
		reasonCode: "agent_profile.not_found",
		status: "unavailable_for_wake",
		summary: "This agent profile no longer exists.",
		tooltip: "Agent profile deleted"
	};
	if (hasHostedCenterAgentDisconnectedReason(readiness)) return {
		action: "none",
		badgeLabel: "Disconnected",
		category: "unavailable",
		isRefreshing: false,
		label: "Disconnected",
		phase: "unavailable",
		reasonCode: pickReasonCode(readiness, [
			"center_agent.designation_required",
			"center_agent.designation_lookup_failed",
			"center_agent.designation_store_unavailable"
		]),
		status: "unavailable_for_wake",
		summary: "Connect the Center Agent before waking it from this Space.",
		tooltip: "Disconnected"
	};
	if (readiness.displayState === "ready" || readiness.facts.runtime.ready || hasReasonCode(readiness, "remote_agent.available")) return {
		action: "none",
		badgeLabel: "Available",
		category: "available",
		isRefreshing: false,
		label: "Available for Wake",
		phase: "available",
		reasonCode: "remote_agent.available",
		status: "available_for_wake",
		summary: "Remote Agent can answer now.",
		tooltip: "Remote Agent available"
	};
	if (reasonCode === "remote_agent.budget_resting") return {
		action: "none",
		badgeLabel: "Resting",
		category: "unavailable",
		isRefreshing: false,
		label: "Remote Agent resting",
		phase: "unavailable",
		reasonCode,
		status: "unavailable_for_wake",
		summary: "Remote Agent is resting until the next budget window.",
		tooltip: "Remote Agent resting"
	};
	return {
		action: "none",
		badgeLabel: "Unavailable",
		category: "unavailable",
		isRefreshing: false,
		label: "Remote Agent unavailable",
		phase: "unavailable",
		reasonCode,
		status: "unavailable_for_wake",
		summary: "Remote Agent cannot answer right now.",
		tooltip: "Remote Agent unavailable"
	};
}
function resolveAgentWakeAvailabilityDisplayCategory(availability) {
	return availability?.category ?? "checking";
}
function createAvailableWakeAvailability() {
	return {
		action: "none",
		badgeLabel: "Available",
		category: "available",
		isRefreshing: false,
		label: "Available for Wake",
		phase: "available",
		reasonCode: "agent_execution.ready",
		status: "available_for_wake",
		summary: "Can reply from this computer in Spaces.",
		tooltip: "Available for Wake"
	};
}
function hasHostedCenterAgentDisconnectedReason(readiness) {
	return [
		"center_agent.designation_required",
		"center_agent.designation_lookup_failed",
		"center_agent.designation_store_unavailable"
	].some((reasonCode) => hasReasonCode(readiness, reasonCode));
}
function createCheckingAgentConnectionAvailability(input) {
	return createAvailability(input.readiness, {
		badgeLabel: "Checking",
		category: "checking",
		isRefreshing: true,
		label: "Checking connection",
		phase: "checking_route",
		reasonCode: input.reasonCode,
		status: "checking_connection",
		summary: "Checking this agent's connection.",
		tooltip: "Checking agent connection"
	});
}
function createOfflineWakeAvailability(input) {
	const serviceReasonCode = pickKnownReasonCode(input.readiness, serviceNotConnectedReasonCodes);
	if (serviceReasonCode) return createServiceNotConnectedAvailability({
		readiness: input.readiness,
		reasonCode: serviceReasonCode
	});
	return createOfflineAgentConnectionAvailability({
		readiness: input.readiness,
		reasonCode: pickKnownReasonCode(input.readiness, routeOfflineReasonCodes) ?? input.routeReasonCode ?? "agent_execution.route_offline"
	});
}
function createServiceNotConnectedAvailability(input) {
	return createAvailability(input.readiness, {
		badgeLabel: "Offline",
		category: "offline",
		isRefreshing: false,
		label: "Offline for Wake",
		phase: "service_not_connected",
		reasonCode: input.reasonCode,
		status: "offline_for_wake",
		summary: "Connect WakeLoop Service on this computer before this agent can reply.",
		tooltip: "WakeLoop Service is not connected on this computer."
	});
}
function createOfflineAgentConnectionAvailability(input) {
	return createAvailability(input.readiness, {
		badgeLabel: "Offline",
		category: "offline",
		isRefreshing: false,
		label: "Offline for Wake",
		phase: "offline",
		reasonCode: input.reasonCode,
		status: "offline_for_wake",
		summary: "This agent's connection is offline.",
		tooltip: "Agent connection is offline"
	});
}
function createWakeRouteUnavailableAvailability(readiness) {
	return createAvailability(readiness, {
		badgeLabel: "Unavailable",
		category: "unavailable",
		isRefreshing: false,
		label: "Unavailable for Wake",
		phase: "unavailable",
		reasonCode: pickReasonCode(readiness, [
			"agent_execution.route_unavailable",
			"route.unavailable",
			"lookup_failed"
		]),
		status: "unavailable_for_wake",
		summary: "This agent's Wake route is unavailable right now.",
		tooltip: "Wake route unavailable"
	});
}
function createCheckingWakeAvailability(input) {
	if (isRefreshableRouteGap(input) || isReadyRouteMissingDispatchClaim(input)) return createAvailability(input.readiness, {
		badgeLabel: "Checking",
		category: "checking",
		isRefreshing: true,
		label: "Checking connection",
		phase: "checking_route",
		reasonCode: pickRouteGapReasonCode(input.readiness),
		status: "checking_connection",
		summary: "Checking this agent's Wake route.",
		tooltip: "Checking Wake route"
	});
	if (input.readiness.facts.runtime.source === "missing") return createAvailability(input.readiness, {
		badgeLabel: "Checking",
		category: "checking",
		isRefreshing: true,
		label: "Checking connection",
		phase: "checking_runtime",
		reasonCode: pickReasonCode(input.readiness, ["runtime.agent_controller_report_stale", "runtime.projection_missing"]),
		status: "checking_connection",
		summary: "Checking this agent's local controller.",
		tooltip: "Checking local agent"
	});
	if (input.readiness.facts.localService.source === "not_checked" || input.readiness.facts.service.source === "not_checked") return createAvailability(input.readiness, {
		badgeLabel: "Checking",
		category: "checking",
		isRefreshing: true,
		label: "Checking connection",
		phase: "checking_service",
		reasonCode: pickReasonCode(input.readiness, ["local_service.not_checked", "service.not_checked"]),
		status: "checking_connection",
		summary: "Checking WakeLoop Service.",
		tooltip: "Checking WakeLoop Service"
	});
	return createAvailability(input.readiness, {
		badgeLabel: "Checking",
		category: "checking",
		isRefreshing: true,
		label: "Checking connection",
		phase: "checking_status",
		reasonCode: pickReasonCode(input.readiness, ["agent_execution.check_status"]),
		status: "checking_connection",
		summary: "Checking this agent's Wake availability.",
		tooltip: "Checking Wake availability"
	});
}
function createWakeRouteRegistrationAvailability(input) {
	const registration = input.agentWakeRouteRegistration ?? null;
	if (!registration || registration.status === "registered") return null;
	if (registration.status === "pending") {
		if (!(input.readiness.facts.localService.reachable && input.readiness.facts.localService.refreshable)) return null;
		return createAvailability(input.readiness, {
			badgeLabel: "Checking",
			category: "checking",
			isRefreshing: true,
			label: "Checking connection",
			phase: "checking_route",
			reasonCode: registration.reasonCode ?? "wake_route.registration_pending",
			status: "checking_connection",
			summary: "Checking this agent's Wake route.",
			tooltip: "Checking Wake route"
		});
	}
	if (registration.status === "expired") return createOfflineWakeAvailability({
		readiness: input.readiness,
		routeReasonCode: registration.reasonCode ?? "wake_route.registration_expired"
	});
	if (registration.status === "skipped") return createAvailability(input.readiness, {
		badgeLabel: "Connect",
		category: "unavailable",
		isRefreshing: false,
		label: "Connect local agent",
		phase: "connect_local_agent",
		reasonCode: registration.reasonCode ?? "wake_route.registration_skipped",
		status: "unavailable_for_wake",
		summary: "Connect a local agent before this profile can reply.",
		tooltip: "Connect local agent"
	});
	if (registration.status === "rejected") return createAvailability(input.readiness, {
		badgeLabel: "Unavailable",
		category: "unavailable",
		isRefreshing: false,
		label: "Unavailable for Wake",
		phase: "unavailable",
		reasonCode: registration.reasonCode ?? "wake_route.registration_rejected",
		status: "unavailable_for_wake",
		summary: "Resolve this agent's Wake route before it can reply.",
		tooltip: "Wake route unavailable"
	});
	return createAvailability(input.readiness, {
		badgeLabel: "Unavailable",
		category: "unavailable",
		isRefreshing: false,
		label: "Unavailable for Wake",
		phase: "unavailable",
		reasonCode: registration.reasonCode ?? "wake_route.registration_unavailable",
		status: "unavailable_for_wake",
		summary: "This agent's Wake route is unavailable right now.",
		tooltip: "Wake route unavailable"
	});
}
function createUnavailableWakeAvailability(readiness) {
	if (readiness.primaryAction === "connect_service") return createServiceNotConnectedAvailability({
		readiness,
		reasonCode: pickKnownReasonCode(readiness, serviceNotConnectedReasonCodes) ?? pickReasonCode(readiness, serviceNotConnectedReasonCodes) ?? "service.not_running"
	});
	if (readiness.primaryAction === "reconnect_binding" || readiness.primaryAction === "run_prepare") return createAvailability(readiness, {
		badgeLabel: "Connect",
		category: "unavailable",
		isRefreshing: false,
		label: "Connect local agent",
		phase: "connect_local_agent",
		reasonCode: pickReasonCode(readiness, [
			"agent_profile_binding.missing",
			"profile.unbound",
			"claim_eligibility.binding_missing"
		]),
		status: "unavailable_for_wake",
		summary: "Connect a local agent before this profile can reply.",
		tooltip: "Connect local agent"
	});
	if (readiness.primaryAction === "fix_provider_account") return createAvailability(readiness, {
		badgeLabel: "Blocked",
		category: "unavailable",
		isRefreshing: false,
		label: "Provider access needed",
		phase: "provider_blocked",
		reasonCode: pickReasonCode(readiness, [
			"provider_account.subscription_access_unavailable",
			"provider_account.organization_access_disabled",
			"provider_account.access_required"
		]),
		status: "unavailable_for_wake",
		summary: "Fix provider access before this agent can reply.",
		tooltip: "Provider access needed"
	});
	if (hasReasonCode(readiness, "agent_profile_binding.controller_conversation_conflict")) return createAvailability(readiness, {
		badgeLabel: "Unavailable",
		category: "unavailable",
		isRefreshing: false,
		label: "Unavailable for Wake",
		phase: "unavailable",
		reasonCode: "agent_profile_binding.controller_conversation_conflict",
		status: "unavailable_for_wake",
		summary: DUPLICATE_REPLY_CONVERSATION_REASON,
		tooltip: DUPLICATE_REPLY_CONVERSATION_TOOLTIP
	});
	return createAvailability(readiness, {
		badgeLabel: "Unavailable",
		category: "unavailable",
		isRefreshing: false,
		label: "Unavailable for Wake",
		phase: "unavailable",
		reasonCode: pickReasonCode(readiness, [
			"profile.inactive",
			"profile.unbound",
			"runtime.agent_controller_report_stale",
			"agent_profile_binding.controller_conversation_conflict",
			"agent_profile_binding.missing",
			"route.unavailable",
			"agent_execution.route_unavailable"
		]),
		status: "unavailable_for_wake",
		summary: "This agent cannot reply from this computer right now.",
		tooltip: "Unavailable for Wake"
	});
}
function hasReasonCode(readiness, reasonCode) {
	return collectReasonCodes(readiness).includes(reasonCode);
}
function classifyAgentWakeAvailabilityReason(reasonCode) {
	if (isPublicUnavailableReasonCode(reasonCode)) return "blocking_unavailable";
	if (isPublicOfflineReasonCode(reasonCode)) return "blocking_offline";
	if (isTransientCheckingReasonCode(reasonCode)) return "transient_checking";
	return "diagnostic";
}
function createAvailability(readiness, values) {
	return {
		...values,
		action: readiness.primaryAction
	};
}
function isCheckingAgentWakeAvailability(input) {
	if (requiresServiceUpdate(input.readiness)) return false;
	if (input.wakeRouteStatus.status === "unavailable") return false;
	if (input.agentWakeRouteRegistration && !["pending", "registered"].includes(input.agentWakeRouteRegistration.status)) return false;
	return isRefreshableAgentExecutionCheck(input) || isRefreshableRouteGap(input) || isReadyRouteMissingDispatchClaim(input);
}
function isAgentWakeAvailable(input) {
	return isWakeRouteReady(input.wakeRouteStatus) && (input.agentWakeRouteRegistration?.status ?? "registered") === "registered" && !hasTransientRouteGap(input.readiness) && !hasHardUnavailableReadinessForReadyRoute(input.readiness);
}
function hasTransientRouteGap(readiness) {
	return collectReasonCodes(readiness).some(isTransientRouteReason);
}
function hasHardUnavailableReadinessForReadyRoute(readiness) {
	const reasonCodes = new Set(collectReasonCodes(readiness));
	for (const reasonCode of reasonCodes) if (isReadyRouteBlockingUnavailableReason(reasonCode)) return true;
	return false;
}
function isReadyRouteBlockingUnavailableReason(reasonCode) {
	if (reasonCode === "agent_execution.route_unavailable" || reasonCode === "route.unavailable") return false;
	return isPublicUnavailableReadinessReason(reasonCode);
}
function isRefreshableAgentExecutionCheck(input) {
	return input.readiness.primaryAction === "check_status" && input.readiness.facts.localService.reachable && input.readiness.facts.localService.refreshable;
}
function isRefreshableRouteGap(input) {
	return input.wakeRouteStatus.status !== "unavailable" && input.readiness.facts.localService.reachable && input.readiness.facts.localService.refreshable && input.readiness.reasonCodes.some(isTransientRouteReason);
}
function requiresServiceUpdate(readiness) {
	return readiness.primaryAction === "update_service" || readiness.reasonCodes.includes("service.update_required");
}
function hasKnownPublicUnavailableReadiness(readiness) {
	if (requiresServiceUpdate(readiness)) return true;
	const reasonCodes = /* @__PURE__ */ new Set([
		...readiness.reasonCodes,
		...readiness.facts.profile.reasonCodes,
		...readiness.facts.binding.reasonCodes,
		...readiness.facts.claim.reasonCodes,
		...readiness.facts.providerAccount.reasonCodes,
		...readiness.facts.providerConversation.reasonCodes,
		...readiness.facts.readinessImpact.reasonCodes,
		...readiness.facts.service.reasonCodes,
		...readiness.facts.runtime.reasonCodes
	]);
	for (const reasonCode of reasonCodes) if (isPublicUnavailableReadinessReason(reasonCode)) return true;
	return false;
}
function pickReasonCode(readiness, preferredReasonCodes) {
	const reasonCodes = collectReasonCodes(readiness);
	for (const preferredReasonCode of preferredReasonCodes) if (reasonCodes.includes(preferredReasonCode)) return preferredReasonCode;
	return reasonCodes[0] ?? null;
}
function pickHostedAgentReasonCode(readiness) {
	return collectReasonCodes(readiness).find((reasonCode) => reasonCode.startsWith("remote_agent.")) ?? null;
}
function pickKnownReasonCode(readiness, preferredReasonCodes) {
	const reasonCodes = collectReasonCodes(readiness);
	for (const preferredReasonCode of preferredReasonCodes) if (reasonCodes.includes(preferredReasonCode)) return preferredReasonCode;
	return null;
}
function pickRouteGapReasonCode(readiness) {
	const reasonCodes = collectReasonCodes(readiness);
	for (const routeReasonCode of ["agent_execution.route_missing", "route.not_registered"]) if (reasonCodes.includes(routeReasonCode)) return routeReasonCode;
	return "agent_execution.route_missing";
}
function collectReasonCodes(readiness) {
	return [
		...readiness.reasonCodes,
		...readiness.facts.profile.reasonCodes,
		...readiness.facts.binding.reasonCodes,
		...readiness.facts.claim.reasonCodes,
		...readiness.facts.providerAccount.reasonCodes,
		...readiness.facts.providerConversation.reasonCodes,
		...readiness.facts.readinessImpact.reasonCodes,
		...readiness.facts.localService.reasonCodes,
		...readiness.facts.service.reasonCodes,
		...readiness.facts.route.reasonCodes,
		...readiness.facts.runtime.reasonCodes
	];
}
function hasKnownPublicOfflineReadiness(input) {
	if (input.wakeRouteStatus.status === "offline") return true;
	const reasonCodes = /* @__PURE__ */ new Set([
		...input.readiness.reasonCodes,
		...input.readiness.facts.localService.reasonCodes,
		...input.readiness.facts.route.reasonCodes
	]);
	for (const reasonCode of reasonCodes) if (isPublicOfflineReadinessReason(reasonCode)) return true;
	return false;
}
function isPublicOfflineReadinessReason(reasonCode) {
	return classifyAgentWakeAvailabilityReason(reasonCode) === "blocking_offline";
}
function isPublicUnavailableReadinessReason(reasonCode) {
	return classifyAgentWakeAvailabilityReason(reasonCode) === "blocking_unavailable";
}
function isPublicOfflineReasonCode(reasonCode) {
	return reasonCode === "local_service.not_connected_for_account" || reasonCode === "local_service.unavailable" || reasonCode === "service.not_running" || reasonCode === "service.current_runtime_session_missing" || reasonCode === "daemon.session.not_found" || reasonCode === "agent_execution.route_offline" || reasonCode === "route.offline";
}
function isPublicUnavailableReasonCode(reasonCode) {
	return reasonCode === "profile.unbound" || reasonCode === "profile.inactive" || reasonCode === "route.unavailable" || reasonCode === "agent_execution.route_unavailable" || reasonCode === "service.update_required" || reasonCode === "background_helper.update_required" || isPublicUnavailableAgentProfileBindingReason(reasonCode) || isPublicUnavailableClaimEligibilityReason(reasonCode) || reasonCode.startsWith("controller.") || reasonCode.startsWith("daemon.runtime_contract.") || reasonCode.startsWith("provider_account.") || reasonCode.startsWith("workspace.");
}
function isPublicUnavailableAgentProfileBindingReason(reasonCode) {
	return reasonCode === "agent_profile_binding.missing" || reasonCode === "agent_profile_binding.disabled_historical" || reasonCode === "agent_profile_binding.conflict" || reasonCode === "agent_profile_binding.owner_mismatch" || reasonCode === "agent_profile_binding.controller_conversation_conflict" || reasonCode === "agent_profile_binding.profile_owner_invalid" || reasonCode === "agent_profile_binding.profile_owner_unavailable" || reasonCode === "agent_profile_binding.read_unavailable" || reasonCode === "agent_profile_binding.store_unavailable" || reasonCode === "agent_profile_binding.runtime_owner_mismatch" || reasonCode === "agent_profile_binding.agent_controller_report_missing" || reasonCode === "agent_profile_binding.agent_controller_report_invalid" || reasonCode === "agent_profile_binding.agent_controller_report_stale" || reasonCode === "agent_profile_binding.agent_controller_report_disabled" || reasonCode === "agent_profile_binding.agent_controller_report_unavailable";
}
function isPublicUnavailableClaimEligibilityReason(reasonCode) {
	return reasonCode === "claim_eligibility.binding_capability_mismatch" || reasonCode === "claim_eligibility.binding_conflict" || reasonCode === "claim_eligibility.binding_disabled" || reasonCode === "claim_eligibility.binding_missing" || reasonCode === "claim_eligibility.binding_not_routable" || reasonCode === "claim_eligibility.binding_owner_mismatch" || reasonCode === "claim_eligibility.binding_runtime_mismatch";
}
function isTransientRouteReason(reasonCode) {
	return classifyAgentWakeAvailabilityReason(reasonCode) === "transient_checking";
}
function isTransientCheckingReasonCode(reasonCode) {
	return reasonCode === "agent_execution.route_missing" || reasonCode === "route.not_registered";
}
function isReadyRouteMissingDispatchClaim(input) {
	return input.wakeRouteStatus.status === "ready" && !isWakeRouteReady(input.wakeRouteStatus);
}
function isWakeRouteReady(wakeRouteStatus) {
	if (wakeRouteStatus.status !== "ready") return false;
	const route = wakeRouteStatus;
	return isNonEmptyString$1(route.daemonSessionId) && isNonEmptyString$1(route.deviceId) && isNonEmptyString$1(route.lane) && isPositiveInteger(route.connectionGeneration) && isPositiveInteger(route.leaseEpoch) && isNonNegativeInteger(route.lastHeartbeatMs);
}
function isNonEmptyString$1(value) {
	return typeof value === "string" && value.trim().length > 0;
}
function isPositiveInteger(value) {
	return Number.isInteger(value) && Number(value) > 0;
}
function isNonNegativeInteger(value) {
	return Number.isInteger(value) && Number(value) >= 0;
}
//#endregion
//#region src/claim-eligibility.ts
const ZERO_SIDE_EFFECTS = {
	leaseCreated: false,
	leaseRenewed: false,
	leaseRejected: false,
	attemptCreated: false,
	resultAccepted: false,
	resultRejected: false,
	runtimeExecutionStarted: false,
	queueConsumed: false,
	webSocketHintAuthorized: false
};
const deny = (reasonCode) => ({
	eligibleForCoreClaimDecision: false,
	grantsClaimSuccess: false,
	reasonCodes: [reasonCode],
	sideEffects: ZERO_SIDE_EFFECTS
});
const resolveAuthorityWakeLoopServiceRuntimeId = (input) => {
	const value = input.wakeLoopServiceRuntimeId;
	if (!(value && typeof value === "object")) return null;
	const candidate = value;
	try {
		return createAuthorityWakeLoopServiceRuntimeId({
			lookupInput: input.lookupInput,
			wakeLoopServiceRuntimeId: candidate.value ?? "",
			source: candidate.source ?? ""
		});
	} catch {
		return null;
	}
};
const REASON_CODE_BY_AGENT_CONTROLLER_REPORT_STATUS = {
	disabled: "claim_eligibility.agent_controller_report_disabled",
	missing: "claim_eligibility.agent_controller_report_missing",
	stale: "claim_eligibility.agent_controller_report_stale",
	unavailable: "claim_eligibility.agent_controller_report_unavailable"
};
const REASON_CODE_BY_BINDING_STATE = {
	conflict: "claim_eligibility.binding_conflict",
	disabled_historical: "claim_eligibility.binding_disabled",
	enabled_primary: "claim_eligibility.binding_not_routable",
	missing: "claim_eligibility.binding_missing",
	owner_mismatch: "claim_eligibility.binding_owner_mismatch"
};
const REASON_CODE_BY_SPACE_CREATION_STATUS = {
	missing: "claim_eligibility.dispatch_evidence_missing",
	policy_denied: "claim_eligibility.wake_not_allowed_at_creation",
	stale: "claim_eligibility.dispatch_evidence_stale"
};
const REASON_CODE_BY_SPACE_LIVENESS_STATUS = {
	missing: "claim_eligibility.space_liveness_missing",
	policy_denied: "claim_eligibility.wake_no_longer_allowed_by_policy",
	policy_version_mismatch: "claim_eligibility.policy_version_mismatch",
	stale: "claim_eligibility.space_liveness_stale",
	target_no_longer_member: "claim_eligibility.target_no_longer_member"
};
const REASON_CODE_BY_DISPATCH_STATE = {
	lease_conflict: "claim_eligibility.lease_conflict",
	missing: "claim_eligibility.dispatch_missing",
	terminal: "claim_eligibility.dispatch_terminal"
};
const resolveClaimEligibility = (input) => {
	if (input.callerAuthorization.status !== "allowed") return deny(input.callerAuthorization.reasonCode);
	if (!input.ownerScope.sameOwner) return deny(input.ownerScope.reasonCode ?? "claim_eligibility.owner_mismatch");
	if (!input.wakeLoopServiceRuntimeIdentity.wakeLoopServiceRuntimeId) return deny("claim_eligibility.wakeloop_service_runtime_id_missing");
	if (!resolveAuthorityWakeLoopServiceRuntimeId(input.wakeLoopServiceRuntimeIdentity)) return deny("claim_eligibility.wakeloop_service_runtime_id_not_opaque");
	if (input.executionTarget.status !== "ready") return deny(input.executionTarget.reasonCode ?? REASON_CODE_BY_AGENT_CONTROLLER_REPORT_STATUS[input.executionTarget.status]);
	if (!input.bindingEligibility) return deny("claim_eligibility.binding_missing");
	if (input.bindingEligibility.bindingState !== "enabled_primary" || !input.bindingEligibility.routable) return deny(input.bindingEligibility.reasonCodes.find((reasonCode) => reasonCode.startsWith("claim_eligibility.")) ?? REASON_CODE_BY_BINDING_STATE[input.bindingEligibility.bindingState]);
	if (input.spaceCreationEvidence.relation !== "wake") return deny("claim_eligibility.non_wake_relation_not_dispatchable");
	if (input.spaceCreationEvidence.status !== "valid") return deny(input.spaceCreationEvidence.reasonCode ?? REASON_CODE_BY_SPACE_CREATION_STATUS[input.spaceCreationEvidence.status]);
	if (input.spaceClaimTimeLiveness.status !== "valid") return deny(input.spaceClaimTimeLiveness.reasonCode ?? REASON_CODE_BY_SPACE_LIVENESS_STATUS[input.spaceClaimTimeLiveness.status]);
	if (input.dispatchState.status !== "open") return deny(input.dispatchState.reasonCode ?? REASON_CODE_BY_DISPATCH_STATE[input.dispatchState.status]);
	return {
		eligibleForCoreClaimDecision: true,
		grantsClaimSuccess: false,
		reasonCodes: ["claim_eligibility.eligible"],
		sideEffects: ZERO_SIDE_EFFECTS
	};
};
//#endregion
//#region src/dedupe-window.ts
const DEFAULT_IDEMPOTENCY_WINDOW_MS = 1440 * 60 * 1e3;
const shouldDedupeWithinWindow = (input) => {
	const windowMs = input.windowMs ?? 864e5;
	const elapsedMs = input.nowMs - input.firstSeenAtMs;
	return elapsedMs >= 0 && elapsedMs < windowMs;
};
//#endregion
//#region src/dispatch-timeout.ts
const DEFAULT_AGENT_EXECUTION_TIMEOUT_MS = null;
const DEFAULT_LOCAL_ADAPTER_PROCESS_TIMEOUT_MS = null;
const DEFAULT_DISPATCH_START_TIMEOUT_MS = 3e4;
const DEFAULT_DISPATCH_WAIT_WINDOW_MS = 3e4;
const MAX_DISPATCH_WAIT_WINDOW_MS = 12e4;
const DEFAULT_TASK_LIFECYCLE_HEARTBEAT_INTERVAL_MS = 15e3;
const DEFAULT_TASK_PROGRESS_EVIDENCE_STALE_AFTER_MS = 9e4;
const DEFAULT_DISPATCH_FINALIZATION_TIMEOUT_MS = 3e4;
//#endregion
//#region src/evidence-sync-health.ts
function resolveEvidenceSyncHealth(input) {
	const generatedAt = input.generatedAt ?? (/* @__PURE__ */ new Date()).toISOString();
	const channels = buildEvidenceSyncHealthChannels({
		generatedAt,
		readiness: input.readiness
	});
	const status = resolveEvidenceSyncHealthStatus(channels);
	return {
		agentProfileId: input.readiness.agentProfileId,
		authoritative: false,
		channels,
		generatedAt,
		scope: "owner_diagnostics",
		source: "evidence_sync_health",
		status,
		summary: summarizeEvidenceSyncHealthStatus(status),
		useInstead: ["AgentWakeAvailability", "wakeloop service trace"],
		verdict: "not_wake_readiness"
	};
}
function buildEvidenceSyncHealthChannels(input) {
	const { facts } = input.readiness;
	return [
		buildProfileIdentityChannel({
			fact: facts.profile,
			generatedAt: input.generatedAt
		}),
		buildBindingProjectionChannel({
			fact: facts.binding,
			generatedAt: input.generatedAt
		}),
		buildLocalServiceChannel({
			fact: facts.localService,
			generatedAt: input.generatedAt
		}),
		buildServiceCompatibilityChannel({
			fact: facts.service,
			generatedAt: input.generatedAt
		}),
		buildRuntimeRegistrationChannel({
			fact: facts.runtime,
			generatedAt: input.generatedAt
		}),
		buildRouteCatalogSyncChannel({
			fact: facts.route,
			generatedAt: input.generatedAt
		}),
		buildExecutionAuthorityDiagnosticChannel({
			fact: facts.claim,
			generatedAt: input.generatedAt
		}),
		buildProviderAccountDiagnosticChannel({
			fact: facts.providerAccount,
			generatedAt: input.generatedAt
		}),
		buildProviderConversationDiagnosticChannel({
			fact: facts.providerConversation,
			generatedAt: input.generatedAt
		}),
		buildReadinessImpactDiagnosticChannel({
			fact: facts.readinessImpact,
			generatedAt: input.generatedAt
		})
	];
}
function buildProfileIdentityChannel(input) {
	if (input.fact.source === "unavailable") return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes),
		generatedAt: input.generatedAt,
		id: "profile_identity",
		label: "Profile identity",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "degraded",
		summary: "Profile identity evidence could not be read."
	});
	if (!input.fact.active) return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes) ?? "agent_profile.inactive",
		generatedAt: input.generatedAt,
		id: "profile_identity",
		label: "Profile identity",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "blocked",
		summary: "Profile identity evidence says this profile is not active."
	});
	return createChannel({
		generatedAt: input.generatedAt,
		id: "profile_identity",
		label: "Profile identity",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: input.fact.source === "not_checked" ? "not_checked" : "healthy",
		summary: input.fact.source === "not_checked" ? "Profile identity was not checked in this diagnostic read." : "Profile identity evidence is available."
	});
}
function buildBindingProjectionChannel(input) {
	if (input.fact.routable) return createChannel({
		generatedAt: input.generatedAt,
		id: "binding_projection",
		label: "Binding projection",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "healthy",
		summary: "Binding projection is routable."
	});
	return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes) ?? "agent_profile_binding.missing",
		generatedAt: input.generatedAt,
		id: "binding_projection",
		label: "Binding projection",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: input.fact.source === "not_checked" ? "not_checked" : "blocked",
		summary: input.fact.source === "not_checked" ? "Binding projection was not checked in this diagnostic read." : "Binding projection is not routable."
	});
}
function buildLocalServiceChannel(input) {
	if (input.fact.reachable) return createChannel({
		generatedAt: input.generatedAt,
		id: "local_service",
		label: "Local WakeLoop Service",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "healthy",
		summary: input.fact.refreshable ? "Local WakeLoop Service evidence is reachable and refreshable." : "Local WakeLoop Service evidence is reachable."
	});
	return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes),
		generatedAt: input.generatedAt,
		id: "local_service",
		label: "Local WakeLoop Service",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: input.fact.source === "not_checked" ? "not_checked" : "degraded",
		summary: input.fact.source === "not_checked" ? "Local WakeLoop Service evidence was not checked in this diagnostic read." : "Local WakeLoop Service evidence is not reachable."
	});
}
function buildServiceCompatibilityChannel(input) {
	if (input.fact.executionCompatible) return createChannel({
		generatedAt: input.generatedAt,
		id: "service_compatibility",
		label: "Service compatibility",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: input.fact.source === "not_checked" ? "not_checked" : "healthy",
		summary: input.fact.source === "not_checked" ? "Service compatibility was not checked in this diagnostic read." : "WakeLoop Service execution contract is compatible."
	});
	return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes) ?? "service.execution_incompatible",
		generatedAt: input.generatedAt,
		id: "service_compatibility",
		label: "Service compatibility",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "blocked",
		summary: "WakeLoop Service execution contract is not compatible."
	});
}
function buildRuntimeRegistrationChannel(input) {
	if (input.fact.ready) return createChannel({
		generatedAt: input.generatedAt,
		id: "runtime_registration",
		label: "Runtime registration",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "healthy",
		summary: "Runtime registration evidence is ready."
	});
	return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes),
		generatedAt: input.generatedAt,
		id: "runtime_registration",
		label: "Runtime registration",
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: input.fact.source === "missing" ? "degraded" : "blocked",
		summary: "Runtime registration evidence is not ready."
	});
}
function buildRouteCatalogSyncChannel(input) {
	if (input.fact.online) return createChannel({
		generatedAt: input.generatedAt,
		id: "route_catalog_sync",
		label: "Route catalog sync",
		lastSuccessAt: input.fact.observedAt,
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "healthy",
		summary: "Route catalog has an online route for this profile."
	});
	return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes),
		generatedAt: input.generatedAt,
		id: "route_catalog_sync",
		label: "Route catalog sync",
		lastSuccessAt: input.fact.observedAt,
		reasonCodes: input.fact.reasonCodes,
		role: "wake_path_evidence",
		source: input.fact.source,
		status: "degraded",
		summary: "Route catalog does not currently have an online route."
	});
}
function buildExecutionAuthorityDiagnosticChannel(input) {
	if (!input.fact.executionBlocked) return createChannel({
		generatedAt: input.generatedAt,
		id: "execution_authority_diagnostic",
		label: "Execution authority diagnostic",
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: input.fact.source === "not_checked" ? "not_checked" : "healthy",
		summary: input.fact.source === "not_checked" ? "Execution authority was not checked in this diagnostic read." : "Execution authority diagnostics do not currently block execution."
	});
	return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes),
		generatedAt: input.generatedAt,
		id: "execution_authority_diagnostic",
		label: "Execution authority diagnostic",
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: "blocked",
		summary: "Execution authority diagnostics currently block execution."
	});
}
function buildProviderAccountDiagnosticChannel(input) {
	if (input.fact.blocked) return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes),
		generatedAt: input.generatedAt,
		id: "provider_account_diagnostic",
		label: "Provider account diagnostic",
		lastSuccessAt: fromEpochMs(input.fact.observedAtMs),
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: "blocked",
		summary: "Provider account diagnostics currently block Wake."
	});
	return createChannel({
		generatedAt: input.generatedAt,
		id: "provider_account_diagnostic",
		label: "Provider account diagnostic",
		lastSuccessAt: fromEpochMs(input.fact.observedAtMs),
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: input.fact.source === "not_checked" ? "not_checked" : "healthy",
		summary: input.fact.source === "not_checked" ? "Provider account diagnostics were not checked in this diagnostic read." : "Provider account diagnostics do not currently block Wake."
	});
}
function buildProviderConversationDiagnosticChannel(input) {
	if (input.fact.status === "not_required") return createChannel({
		generatedAt: input.generatedAt,
		id: "provider_conversation_diagnostic",
		label: "Provider conversation diagnostic",
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: "healthy",
		summary: "Provider conversation proof is not required."
	});
	if (input.fact.ready) return createChannel({
		generatedAt: input.generatedAt,
		id: "provider_conversation_diagnostic",
		label: "Provider conversation diagnostic",
		lastSuccessAt: fromEpochMs(input.fact.observedAtMs),
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: "healthy",
		summary: "Provider conversation proof is ready."
	});
	return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes) ?? input.fact.errorCode ?? "provider_conversation.not_ready",
		generatedAt: input.generatedAt,
		id: "provider_conversation_diagnostic",
		label: "Provider conversation diagnostic",
		lastSuccessAt: fromEpochMs(input.fact.observedAtMs),
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: "degraded",
		summary: input.fact.required ? "Provider conversation proof is required but not ready." : "Provider conversation proof is diagnostic and not ready."
	});
}
function buildReadinessImpactDiagnosticChannel(input) {
	if (input.fact.blocked) return createChannel({
		failureCode: firstReasonCode(input.fact.reasonCodes),
		generatedAt: input.generatedAt,
		id: "readiness_impact_diagnostic",
		label: "Readiness impact diagnostic",
		lastSuccessAt: fromEpochMs(input.fact.observedAtMs),
		nextRetryAt: fromEpochMs(input.fact.expiresAtMs),
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: "blocked",
		summary: "Recent Wake failure evidence currently blocks readiness."
	});
	return createChannel({
		generatedAt: input.generatedAt,
		id: "readiness_impact_diagnostic",
		label: "Readiness impact diagnostic",
		lastSuccessAt: fromEpochMs(input.fact.observedAtMs),
		nextRetryAt: fromEpochMs(input.fact.expiresAtMs),
		reasonCodes: input.fact.reasonCodes,
		role: "owner_diagnostic",
		source: input.fact.source,
		status: input.fact.source === "not_checked" ? "not_checked" : "healthy",
		summary: input.fact.source === "not_checked" ? "Readiness impact diagnostics were not checked in this diagnostic read." : "No recent Wake failure impact currently blocks readiness."
	});
}
function createChannel(input) {
	return {
		failureCode: input.failureCode ?? null,
		id: input.id,
		label: input.label,
		lastAttemptAt: input.generatedAt,
		lastSuccessAt: input.lastSuccessAt ?? (input.status === "healthy" ? input.generatedAt : null),
		nextRetryAt: input.nextRetryAt ?? null,
		reasonCodes: input.reasonCodes,
		role: input.role,
		source: input.source,
		status: input.status,
		summary: input.summary
	};
}
function resolveEvidenceSyncHealthStatus(channels) {
	if (channels.some((channel) => channel.status === "blocked")) return "blocked";
	if (channels.some((channel) => channel.status === "degraded")) return "degraded";
	if (channels.every((channel) => channel.status === "not_checked")) return "not_checked";
	return "healthy";
}
function summarizeEvidenceSyncHealthStatus(status) {
	switch (status) {
		case "blocked": return "Owner diagnostics found evidence that needs repair.";
		case "degraded": return "Owner diagnostics found missing or stale evidence.";
		case "healthy": return "Owner diagnostics have current evidence for the Wake path.";
		case "not_checked": return "Owner diagnostics were not checked.";
		default: return "Owner diagnostics are unavailable.";
	}
}
function firstReasonCode(reasonCodes) {
	return reasonCodes[0] ?? null;
}
function fromEpochMs(value) {
	return typeof value === "number" && Number.isFinite(value) ? new Date(value).toISOString() : null;
}
//#endregion
//#region src/health-view.ts
const normalizeExtraFields = (fields) => {
	if (!fields) return {};
	return Object.fromEntries(Object.entries(fields).filter((entry) => entry[1] !== void 0));
};
const buildHealthView = (input) => ({
	finding: {
		reasonCode: input.finding.reasonCode,
		severity: input.finding.severity,
		summaryKey: input.finding.summaryKey
	},
	human: input.finding.humanContext
});
const emitHealthFinding = (input) => {
	const logSink = input.logSink ?? globalThis.console;
	const payload = {
		...buildHealthView({ finding: input.finding }),
		...normalizeExtraFields(input.fields)
	};
	if (input.finding.severity === "error") {
		logSink?.error?.(input.eventName, payload);
		return;
	}
	logSink?.warn?.(input.eventName, payload);
};
//#endregion
//#region src/idempotency-key.ts
const buildIdempotencyKey = (input) => {
	return `${input.sourceEventId.length}:${input.sourceEventId}|${input.targetProfileId.length}:${input.targetProfileId}`;
};
//#endregion
//#region src/lease-fencing.ts
const resolveLeaseArbitration = (input) => {
	if (!input.current) return {
		accepted: true,
		nextLeaseEpoch: 1,
		reason: "new_session"
	};
	if (input.current.daemonSessionId === input.candidate.daemonSessionId) return {
		accepted: true,
		nextLeaseEpoch: input.current.leaseEpoch,
		reason: "same_session"
	};
	if (input.current.deviceId === input.candidate.deviceId && input.current.lane === input.candidate.lane) return {
		accepted: true,
		nextLeaseEpoch: input.current.leaseEpoch + 1,
		reason: "same_device_reconnect"
	};
	if (input.candidate.takeover === true) return {
		accepted: true,
		nextLeaseEpoch: input.current.leaseEpoch + 1,
		reason: "takeover"
	};
	return {
		accepted: false,
		reason: "conflict"
	};
};
const isLeaseMatch = (input) => {
	return input.expectedDaemonSessionId === input.actualDaemonSessionId && input.expectedLeaseEpoch === input.actualLeaseEpoch;
};
//#endregion
//#region ../../config/agents/registry.json
var registry_default = {
	schemaVersion: "1.0.0",
	updatedAt: "2026-03-02T00:00:00.000Z",
	metadata: {
		"maintainer": "WAKELOOP_DAEMON Team",
		"purpose": "Single source of truth for agent detection, skills installability, and runtime support capability."
	},
	agents: [
		{
			"id": "amp",
			"displayName": "Amp",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${XDG_CONFIG_HOME}/agents/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${XDG_CONFIG_HOME}/amp"
				}, {
					"kind": "pathExists",
					"value": "${HOME}/.config/amp"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "adal",
			"displayName": "AdaL",
			"category": "builtin",
			"skillsDir": ".adal/skills",
			"globalSkillsDir": "${HOME}/.adal/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.adal"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "antigravity",
			"displayName": "Antigravity",
			"category": "builtin",
			"skillsDir": ".agent/skills",
			"globalSkillsDir": "${HOME}/.gemini/antigravity/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.gemini/antigravity"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "augment",
			"displayName": "Augment",
			"category": "builtin",
			"skillsDir": ".augment/skills",
			"globalSkillsDir": "${HOME}/.augment/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.augment"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "claude-code",
			"displayName": "Claude Code",
			"category": "builtin",
			"skillsDir": ".claude/skills",
			"globalSkillsDir": "${CLAUDE_CONFIG_DIR}/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.claude"
				}, {
					"kind": "pathExists",
					"value": "${CLAUDE_CONFIG_DIR}"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": true,
				"adapters": [{
					"adapterId": "claude-code",
					"controllerRefMatcher": {
						"kind": "contains",
						"values": ["claude", "claude-code"]
					},
					"transportModes": ["local-cli"],
					"capabilityDeclaration": {
						"streamingCapability": "preview_capable",
						"runtimeWorkingDirectoryMode": "spawn_cwd",
						"preparationRequirement": "none",
						"continuationCapability": "standard",
						"workspaceResumeBehavior": "validate_only"
					}
				}]
			},
			"lifecycle": { "status": "active" },
			"originLaunch": { "commandTemplate": "cd {workingDirectory} && claude --model {model} --resume {agentControllerConversationId}" },
			"transportModes": ["local-cli"],
			"defaultTransportMode": "local-cli",
			"defaultStreamingMode": "final"
		},
		{
			"id": "cline",
			"displayName": "Cline",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${HOME}/.agents/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.cline"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "codex",
			"aliases": ["builtin:codex"],
			"displayName": "Codex",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${CODEX_HOME}/skills",
			"detection": {
				"mode": "any",
				"checks": [
					{
						"kind": "pathExists",
						"value": "${HOME}/.codex"
					},
					{
						"kind": "pathExists",
						"value": "${CODEX_HOME}"
					},
					{
						"kind": "pathExists",
						"value": "/etc/codex"
					}
				]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": true,
				"adapters": [{
					"adapterId": "codex",
					"controllerRefMatcher": {
						"kind": "contains",
						"values": ["codex"]
					},
					"transportModes": ["codex-app-server"],
					"capabilityDeclaration": {
						"streamingCapability": "preview_capable",
						"runtimeWorkingDirectoryMode": "spawn_cwd",
						"preparationRequirement": "none",
						"continuationCapability": "standard",
						"workspaceResumeBehavior": "transparent"
					}
				}]
			},
			"lifecycle": { "status": "active" },
			"originLaunch": { "commandTemplate": "cd {workingDirectory} && codex resume -m {model} {agentControllerConversationId}" },
			"notes": "Primary runnable agent in current environment.",
			"transportModes": ["codex-app-server"],
			"defaultTransportMode": "codex-app-server",
			"defaultStreamingMode": "final"
		},
		{
			"id": "command-code",
			"displayName": "Command Code",
			"category": "builtin",
			"skillsDir": ".commandcode/skills",
			"globalSkillsDir": "${HOME}/.commandcode/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.commandcode"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "codebuddy",
			"displayName": "CodeBuddy",
			"category": "builtin",
			"skillsDir": ".codebuddy/skills",
			"globalSkillsDir": "${HOME}/.codebuddy/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.codebuddy"
				}, {
					"kind": "pathExists",
					"value": "${CWD}/.codebuddy"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "continue",
			"displayName": "Continue",
			"category": "builtin",
			"skillsDir": ".continue/skills",
			"globalSkillsDir": "${HOME}/.continue/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.continue"
				}, {
					"kind": "pathExists",
					"value": "${CWD}/.continue"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "cortex",
			"displayName": "Cortex Code",
			"category": "builtin",
			"skillsDir": ".cortex/skills",
			"globalSkillsDir": "${HOME}/.snowflake/cortex/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.snowflake/cortex"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "crush",
			"displayName": "Crush",
			"category": "builtin",
			"skillsDir": ".crush/skills",
			"globalSkillsDir": "${HOME}/.config/crush/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.config/crush"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "cursor",
			"displayName": "Cursor",
			"category": "builtin",
			"skillsDir": ".cursor/skills",
			"globalSkillsDir": "${HOME}/.cursor/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.cursor"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "droid",
			"displayName": "Droid",
			"category": "builtin",
			"skillsDir": ".factory/skills",
			"globalSkillsDir": "${HOME}/.factory/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.factory"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "gemini-cli",
			"displayName": "Gemini CLI",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${HOME}/.gemini/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.gemini"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "github-copilot",
			"displayName": "GitHub Copilot",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${HOME}/.copilot/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.copilot"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "goose",
			"displayName": "Goose",
			"category": "builtin",
			"skillsDir": ".goose/skills",
			"globalSkillsDir": "${XDG_CONFIG_HOME}/goose/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${XDG_CONFIG_HOME}/goose"
				}, {
					"kind": "pathExists",
					"value": "${HOME}/.config/goose"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "iflow-cli",
			"displayName": "iFlow CLI",
			"category": "builtin",
			"skillsDir": ".iflow/skills",
			"globalSkillsDir": "${HOME}/.iflow/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.iflow"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "junie",
			"displayName": "Junie",
			"category": "builtin",
			"skillsDir": ".junie/skills",
			"globalSkillsDir": "${HOME}/.junie/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.junie"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "kilo",
			"displayName": "Kilo Code",
			"category": "builtin",
			"skillsDir": ".kilocode/skills",
			"globalSkillsDir": "${HOME}/.kilocode/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.kilocode"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "kimi-cli",
			"displayName": "Kimi Code CLI",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${HOME}/.config/agents/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.kimi"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "kiro-cli",
			"displayName": "Kiro CLI",
			"category": "builtin",
			"skillsDir": ".kiro/skills",
			"globalSkillsDir": "${HOME}/.kiro/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.kiro"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "kode",
			"displayName": "Kode",
			"category": "builtin",
			"skillsDir": ".kode/skills",
			"globalSkillsDir": "${HOME}/.kode/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.kode"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "mcpjam",
			"displayName": "MCPJam",
			"category": "builtin",
			"skillsDir": ".mcpjam/skills",
			"globalSkillsDir": "${HOME}/.mcpjam/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.mcpjam"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "mistral-vibe",
			"displayName": "Mistral Vibe",
			"category": "builtin",
			"skillsDir": ".vibe/skills",
			"globalSkillsDir": "${HOME}/.vibe/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.vibe"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "mux",
			"displayName": "Mux",
			"category": "builtin",
			"skillsDir": ".mux/skills",
			"globalSkillsDir": "${HOME}/.mux/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.mux"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "neovate",
			"displayName": "Neovate",
			"category": "builtin",
			"skillsDir": ".neovate/skills",
			"globalSkillsDir": "${HOME}/.neovate/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.neovate"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "opencode",
			"displayName": "OpenCode",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${XDG_CONFIG_HOME}/opencode/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${XDG_CONFIG_HOME}/opencode"
				}, {
					"kind": "pathExists",
					"value": "${HOME}/.config/opencode"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": true,
				"adapters": [{
					"adapterId": "opencode",
					"controllerRefMatcher": {
						"kind": "contains",
						"values": ["opencode", "open-code"]
					},
					"transportModes": ["local-cli"],
					"capabilityDeclaration": {
						"streamingCapability": "final_only",
						"runtimeWorkingDirectoryMode": "cli_flag",
						"preparationRequirement": "none",
						"continuationCapability": "standard",
						"workspaceResumeBehavior": "transparent"
					}
				}]
			},
			"originLaunch": { "commandTemplate": "cd {workingDirectory} && opencode run --session {agentControllerConversationId} --model {model}" },
			"transportModes": ["local-cli"],
			"defaultTransportMode": "local-cli",
			"defaultStreamingMode": "final"
		},
		{
			"id": "openhands",
			"displayName": "OpenHands",
			"category": "builtin",
			"skillsDir": ".openhands/skills",
			"globalSkillsDir": "${HOME}/.openhands/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.openhands"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "openclaw",
			"displayName": "OpenClaw",
			"category": "builtin",
			"skillsDir": "skills",
			"globalSkillsDir": [
				"${HOME}/.openclaw/skills",
				"${HOME}/.clawdbot/skills",
				"${HOME}/.moltbot/skills"
			],
			"detection": {
				"mode": "any",
				"checks": [
					{
						"kind": "pathExists",
						"value": "${HOME}/.openclaw"
					},
					{
						"kind": "pathExists",
						"value": "${HOME}/.clawdbot"
					},
					{
						"kind": "pathExists",
						"value": "${HOME}/.moltbot"
					}
				]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": true,
				"adapters": [{
					"adapterId": "openclaw",
					"controllerRefMatcher": {
						"kind": "contains",
						"values": ["openclaw", "claw"]
					},
					"transportModes": ["local-cli"],
					"capabilityDeclaration": {
						"streamingCapability": "final_only",
						"runtimeWorkingDirectoryMode": "provider_workspace",
						"preparationRequirement": "required",
						"continuationCapability": "provider_specific",
						"workspaceResumeBehavior": "provider_specific_switch"
					}
				}]
			},
			"lifecycle": { "status": "suspended" },
			"originLaunch": { "commandTemplate": "openclaw tui --session {agentControllerConversationId}" },
			"transportModes": ["local-cli"],
			"defaultTransportMode": "local-cli",
			"defaultStreamingMode": "final"
		},
		{
			"id": "pi",
			"displayName": "Pi",
			"category": "builtin",
			"skillsDir": ".pi/skills",
			"globalSkillsDir": "${HOME}/.pi/agent/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.pi/agent"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "pochi",
			"displayName": "Pochi",
			"category": "builtin",
			"skillsDir": ".pochi/skills",
			"globalSkillsDir": "${HOME}/.pochi/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.pochi"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "qoder",
			"displayName": "Qoder",
			"category": "builtin",
			"skillsDir": ".qoder/skills",
			"globalSkillsDir": "${HOME}/.qoder/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.qoder"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "qwen-code",
			"displayName": "Qwen Code",
			"category": "builtin",
			"skillsDir": ".qwen/skills",
			"globalSkillsDir": "${HOME}/.qwen/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.qwen"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "replit",
			"displayName": "Replit",
			"category": "builtin",
			"skillsDir": ".agents/skills",
			"globalSkillsDir": "${XDG_CONFIG_HOME}/agents/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${CWD}/.replit"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			},
			"notes": "Keep detection close to upstream behavior; no universal list enforcement in this registry layer."
		},
		{
			"id": "roo",
			"displayName": "Roo Code",
			"category": "builtin",
			"skillsDir": ".roo/skills",
			"globalSkillsDir": "${HOME}/.roo/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.roo"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "trae",
			"displayName": "Trae",
			"category": "builtin",
			"skillsDir": ".trae/skills",
			"globalSkillsDir": "${HOME}/.trae/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.trae"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "trae-cn",
			"displayName": "Trae CN",
			"category": "builtin",
			"skillsDir": ".trae/skills",
			"globalSkillsDir": "${HOME}/.trae-cn/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.trae-cn"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "windsurf",
			"displayName": "Windsurf",
			"category": "builtin",
			"skillsDir": ".windsurf/skills",
			"globalSkillsDir": "${HOME}/.codeium/windsurf/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.codeium/windsurf"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		},
		{
			"id": "zencoder",
			"displayName": "Zencoder",
			"category": "builtin",
			"skillsDir": ".zencoder/skills",
			"globalSkillsDir": "${HOME}/.zencoder/skills",
			"detection": {
				"mode": "any",
				"checks": [{
					"kind": "pathExists",
					"value": "${HOME}/.zencoder"
				}]
			},
			"service": { "skillsInstallable": true },
			"runtime": {
				"supported": false,
				"adapters": []
			}
		}
	]
};
//#endregion
//#region src/local-agent-catalog.ts
const BUILTIN_LOCAL_AGENT_CATALOG_DEFINITIONS = readBuiltinLocalAgentCatalogDefinitions();
const AGENT_ID_WORD_SEPARATOR_RE = /[-_]+/u;
function projectLocalAgentCatalogFromCapabilities(input) {
	const capabilityByRef = new Map(input.capabilities.flatMap((capability) => {
		const parsed = parseAgentControllerRef(capability.agentControllerRef);
		return parsed ? [[parsed.ref, capability]] : [];
	}));
	const entries = [...BUILTIN_LOCAL_AGENT_CATALOG_DEFINITIONS.map((definition) => projectBuiltinLocalAgentCatalogEntry({
		capability: capabilityByRef.get(`builtin:${definition.agentId}`) ?? null,
		definition
	})), ...projectCustomLocalAgentCatalogEntries({
		capabilities: input.capabilities,
		knownAgentControllerRefs: new Set(capabilityByRef.keys())
	})];
	const builtinEntries = entries.filter((entry) => entry.targetKind === "builtin");
	return {
		detectedNotSupportedYet: builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "not_available" && entry.selectableReason === "not_supported_yet").length,
		detectedSupported: builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "ready").length,
		entries,
		needsChoice: builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "needs_choice").length,
		needsRepair: builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "needs_repair").length,
		notDetected: builtinEntries.filter((entry) => !entry.detected).length,
		totalBuiltins: builtinEntries.length
	};
}
function projectBuiltinLocalAgentCatalogEntry(input) {
	const status = input.capability?.status ?? "unknown";
	const enabled = input.capability?.enabled ?? true;
	if (input.capability && !input.definition.runtimeSupported) return {
		agentId: input.definition.agentId,
		detected: true,
		displayName: input.definition.displayName,
		enabled: false,
		lifecycleStatus: input.definition.lifecycleStatus,
		launchStatus: "not_available",
		selectable: false,
		selectableReason: "not_supported_yet",
		targetKind: "builtin"
	};
	if (input.capability && !enabled) return {
		agentId: input.definition.agentId,
		detected: true,
		displayName: input.definition.displayName,
		enabled: false,
		lifecycleStatus: input.definition.lifecycleStatus,
		launchStatus: "ready",
		selectable: true,
		selectableReason: null,
		targetKind: "builtin"
	};
	if (status === "ready") return {
		agentId: input.definition.agentId,
		detected: true,
		displayName: input.definition.displayName,
		enabled,
		lifecycleStatus: input.definition.lifecycleStatus,
		launchStatus: "ready",
		selectable: true,
		selectableReason: null,
		targetKind: "builtin"
	};
	if (status === "not_ready" || status === "stale") return {
		agentId: input.definition.agentId,
		detected: true,
		displayName: input.definition.displayName,
		enabled,
		lifecycleStatus: input.definition.lifecycleStatus,
		launchStatus: "needs_repair",
		selectable: true,
		selectableReason: null,
		targetKind: "builtin"
	};
	return {
		agentId: input.definition.agentId,
		detected: false,
		displayName: input.definition.displayName,
		enabled: false,
		lifecycleStatus: input.definition.lifecycleStatus,
		launchStatus: "not_available",
		selectable: false,
		selectableReason: "not_detected",
		targetKind: "builtin"
	};
}
function projectCustomLocalAgentCatalogEntries(input) {
	return input.capabilities.flatMap((capability) => {
		const parsed = parseAgentControllerRef(capability.agentControllerRef);
		if (!(parsed && parsed.kind === "custom")) return [];
		if (!input.knownAgentControllerRefs.has(parsed.ref)) return [];
		if (capability.status === "ready") return [{
			agentId: parsed.id,
			detected: true,
			displayName: formatFallbackAgentDisplayName(parsed.id),
			enabled: true,
			lifecycleStatus: "active",
			launchStatus: "ready",
			selectable: true,
			selectableReason: null,
			targetKind: "custom"
		}];
		return [{
			agentId: parsed.id,
			detected: capability.status !== "unknown",
			displayName: formatFallbackAgentDisplayName(parsed.id),
			enabled: false,
			lifecycleStatus: "active",
			launchStatus: "not_available",
			selectable: false,
			selectableReason: "custom_coming_soon",
			targetKind: "custom"
		}];
	});
}
function readBuiltinLocalAgentCatalogDefinitions() {
	const document = registry_default;
	if (!Array.isArray(document.agents)) return [];
	return document.agents.flatMap((agent) => {
		if (!isBuiltinLocalAgentDefinition(agent)) return [];
		return [{
			agentId: agent.id,
			displayName: agent.displayName,
			lifecycleStatus: readRegistryLifecycleStatus(agent.lifecycle),
			runtimeSupported: isRegistryRuntimeSupported(agent.runtime)
		}];
	}).sort((left, right) => left.displayName.localeCompare(right.displayName));
}
function isBuiltinLocalAgentDefinition(value) {
	if (!isRegistryAgentLike(value)) return false;
	if (value.category !== "builtin") return false;
	if (!(isNonEmptyString(value.id) && isNonEmptyString(value.displayName))) return false;
	return true;
}
function isRegistryAgentLike(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isRegistryRuntimeSupported(value) {
	if (!(typeof value === "object" && value !== null && !Array.isArray(value))) return false;
	return value.supported === true;
}
function readRegistryLifecycleStatus(value) {
	if (!(typeof value === "object" && value !== null && !Array.isArray(value))) return "active";
	const status = value.status;
	if (status === "active" || status === "suspended") return status;
	throw new Error(`Invalid agents registry lifecycle status: ${String(status)}`);
}
function isNonEmptyString(value) {
	return typeof value === "string" && value.trim().length > 0;
}
function formatFallbackAgentDisplayName(agentId) {
	return agentId.split(AGENT_ID_WORD_SEPARATOR_RE).filter(Boolean).map((part) => part[0]?.toUpperCase() + part.slice(1)).join(" ");
}
//#endregion
//#region src/model-selection.ts
function resolveConversationPolicyFromModelSelection(input) {
	const selection = createAgentProfileBindingModelSelection(input.modelSelection);
	const parsedRef = parseAgentControllerRef(input.agentControllerRef);
	if (!parsedRef) return {
		status: "accepted",
		conversationPolicy: {}
	};
	const controller = input.catalog.agentControllers[parsedRef.id];
	if (!controller) return {
		status: "accepted",
		conversationPolicy: {}
	};
	const availableModels = controller.models.filter((model) => !model.unavailable);
	const defaultModel = availableModels.find((model) => model.id === controller.defaultModelId) ?? availableModels[0] ?? null;
	if (!defaultModel) return {
		status: "accepted",
		conversationPolicy: {}
	};
	if (!selection) return {
		status: "accepted",
		conversationPolicy: {
			fallbackModel: defaultModel.id,
			model: defaultModel.id
		}
	};
	const selectedModel = availableModels.find((model) => model.id === selection.modelId) ?? null;
	const model = selectedModel ?? defaultModel;
	const reasoningLevel = selectedModel && selection.reasoningLevel && selectedModel.supportedReasoningLevels.includes(selection.reasoningLevel) ? selection.reasoningLevel : null;
	return {
		status: "accepted",
		conversationPolicy: {
			fallbackModel: defaultModel.id,
			model: model.id,
			...reasoningLevel ? { reasoningLevel } : {}
		}
	};
}
//#endregion
//#region src/prepare-readiness.ts
const READY_REASON_CODES = [
	"runtime.ready",
	"capability.ready",
	"service.ready"
];
const DEFAULT_REASON_CODE_BY_STATE = {
	pending: "runtime.projection_pending",
	unavailable: "runtime.projection_unavailable",
	needs_prepare: "runtime.prepare_required",
	needs_repair: "service.needs_repair",
	unknown: "runtime.projection_unknown"
};
const FORBIDDEN_BINDING_STEP_KINDS = /* @__PURE__ */ new Set([
	"bind_agent_profile",
	"create_binding",
	"update_binding",
	"write_binding"
]);
const FORBIDDEN_EXECUTABLE_WORK_STEP_KINDS = /* @__PURE__ */ new Set([
	...FORBIDDEN_BINDING_STEP_KINDS,
	"claim_dispatch",
	"create_space",
	"join_space",
	"send_wake",
	"wake",
	"write_claim",
	"write_result"
]);
const uniqueReasonCodes = (codes) => {
	const seen = /* @__PURE__ */ new Set();
	const normalized = [];
	for (const code of codes) {
		const reasonCode = code.trim();
		if (!(reasonCode && !seen.has(reasonCode))) continue;
		seen.add(reasonCode);
		normalized.push(reasonCode);
	}
	return normalized;
};
const resolvePrepareReadinessFromAgentControllerReports = (input) => {
	const reportByAgentControllerRef = /* @__PURE__ */ new Map();
	for (const report of input.currentReports) {
		const agentControllerRef = normalizeAgentControllerRef(report.agentControllerRef);
		if (agentControllerRef) reportByAgentControllerRef.set(agentControllerRef, report);
	}
	const agentControllerRefsToEvaluate = resolveAgentControllerRefsToEvaluate({
		agentControllerRefsToEvaluate: input.agentControllerRefsToEvaluate,
		reportByAgentControllerRef
	});
	const runnableCapabilities = [];
	const nonRunnableCapabilities = agentControllerRefsToEvaluate.invalid.map((agentControllerRef) => ({
		agentControllerRef,
		reasonCodes: ["runtime.agent_controller_ref_invalid"],
		status: "invalid"
	}));
	for (const agentControllerRef of agentControllerRefsToEvaluate.valid) {
		const report = reportByAgentControllerRef.get(agentControllerRef);
		if (!report) {
			nonRunnableCapabilities.push({
				agentControllerRef,
				reasonCodes: ["runtime.agent_controller_report_missing"],
				status: "missing"
			});
			continue;
		}
		if (report.expiresAtMs <= input.nowMs) {
			nonRunnableCapabilities.push({
				agentControllerRef,
				expiresAtMs: report.expiresAtMs,
				observedAtMs: report.observedAtMs,
				reasonCodes: uniqueReasonCodes(["runtime.agent_controller_report_stale", ...report.reasonCodes ?? []]),
				status: "stale"
			});
			continue;
		}
		if (report.status === "ready") {
			runnableCapabilities.push({
				agentControllerRef,
				expiresAtMs: report.expiresAtMs,
				observedAtMs: report.observedAtMs,
				reasonCodes: uniqueReasonCodes(report.reasonCodes ?? []),
				status: "ready"
			});
			continue;
		}
		nonRunnableCapabilities.push({
			agentControllerRef,
			expiresAtMs: report.expiresAtMs,
			observedAtMs: report.observedAtMs,
			reasonCodes: uniqueReasonCodes([`runtime.agent_controller_report_${report.status}`, ...report.reasonCodes ?? []]),
			status: report.status
		});
	}
	const projection = resolveAgentControllerReportProjection({
		nonRunnableCapabilities,
		runnableCapabilities,
		serviceReadiness: input.serviceReadiness
	});
	return {
		nonRunnableCapabilities,
		projection,
		runnableCapabilities,
		summary: resolvePrepareReadinessSummary(projection, { now: new Date(input.nowMs) })
	};
};
const normalizeAgentControllerRef = (agentControllerRef) => {
	return parseAgentControllerRef(agentControllerRef)?.ref;
};
const resolveAgentControllerRefsToEvaluate = (input) => {
	const explicitAgentControllerRefs = input.agentControllerRefsToEvaluate ?? [];
	if (explicitAgentControllerRefs.length === 0) return {
		invalid: [],
		valid: Array.from(input.reportByAgentControllerRef.keys()).sort()
	};
	const invalid = [];
	const valid = [];
	for (const agentControllerRef of explicitAgentControllerRefs) {
		const parsed = normalizeAgentControllerRef(agentControllerRef);
		if (parsed) {
			valid.push(parsed);
			continue;
		}
		invalid.push(agentControllerRef.trim());
	}
	return {
		invalid: uniqueReasonCodes(invalid),
		valid: uniqueReasonCodes(valid)
	};
};
const resolveAgentControllerReportProjection = (input) => {
	if (input.nonRunnableCapabilities.length === 0) return {
		allRunnableCapabilitiesPrepared: { status: input.runnableCapabilities.length > 0 ? "prepared" : "unknown" },
		projectionStatus: input.runnableCapabilities.length > 0 ? "ready" : "unknown",
		reasonCodes: input.runnableCapabilities.length > 0 ? [] : ["runtime.agent_controller_report_missing"],
		serviceReadiness: input.serviceReadiness
	};
	return {
		allRunnableCapabilitiesPrepared: { status: input.runnableCapabilities.length > 0 ? "partial" : "not_prepared" },
		projectionStatus: resolveNonRunnableDisplayState(input.nonRunnableCapabilities),
		reasonCodes: uniqueReasonCodes(input.nonRunnableCapabilities.flatMap((capability) => capability.reasonCodes)),
		serviceReadiness: input.serviceReadiness
	};
};
const resolveNonRunnableDisplayState = (capabilities) => {
	if (capabilities.some((capability) => capability.status === "stale")) return "stale";
	if (capabilities.some((capability) => capability.status === "unavailable")) return "unavailable";
	if (capabilities.some((capability) => capability.status === "unknown")) return "unknown";
	return "needs_prepare";
};
const withReasonCodes = (input) => ({
	ready: input.ready,
	displayState: input.displayState,
	reasonCodes: uniqueReasonCodes(input.reasonCodes.length > 0 ? input.reasonCodes : [input.fallbackReasonCode])
});
const isProjectionExpired = (projection, now) => {
	if (!projection.expiresAt) return false;
	const expiresAt = Date.parse(projection.expiresAt);
	if (!Number.isFinite(expiresAt)) return true;
	return expiresAt <= now.getTime();
};
const resolveNow = (value) => {
	if (value instanceof Date) return value;
	if (typeof value === "string") return new Date(value);
	return /* @__PURE__ */ new Date();
};
const resolvePrepareReadinessSummary = (projection, options) => {
	if (!projection) return {
		ready: false,
		displayState: "unknown",
		reasonCodes: ["runtime.projection_missing"]
	};
	if (isProjectionExpired(projection, resolveNow(options?.now))) return withReasonCodes({
		ready: false,
		displayState: "stale",
		reasonCodes: projection.reasonCodes,
		fallbackReasonCode: "runtime.projection_stale"
	});
	if (projection.projectionStatus !== "ready") {
		const displayState = projection.projectionStatus;
		return withReasonCodes({
			ready: false,
			displayState,
			reasonCodes: projection.reasonCodes,
			fallbackReasonCode: displayState === "stale" ? "runtime.projection_stale" : DEFAULT_REASON_CODE_BY_STATE[displayState]
		});
	}
	const allRunnableStatus = projection.allRunnableCapabilitiesPrepared?.status;
	if (allRunnableStatus !== "prepared") {
		const displayState = allRunnableStatus === "not_prepared" || allRunnableStatus === "partial" ? "needs_prepare" : "unknown";
		return withReasonCodes({
			ready: false,
			displayState,
			reasonCodes: projection.reasonCodes,
			fallbackReasonCode: displayState === "needs_prepare" ? "runtime.prepare_required" : "capability.readiness_unknown"
		});
	}
	if (projection.serviceReadiness) {
		const serviceStatus = projection.serviceReadiness.status;
		if (serviceStatus === "needs_repair") return withReasonCodes({
			ready: false,
			displayState: "needs_repair",
			reasonCodes: projection.reasonCodes,
			fallbackReasonCode: "service.needs_repair"
		});
		if (serviceStatus === "unavailable") return withReasonCodes({
			ready: false,
			displayState: "unavailable",
			reasonCodes: projection.reasonCodes,
			fallbackReasonCode: "service.unavailable"
		});
		if (serviceStatus !== "running") return withReasonCodes({
			ready: false,
			displayState: "needs_prepare",
			reasonCodes: projection.reasonCodes,
			fallbackReasonCode: "service.not_running"
		});
	}
	return {
		ready: true,
		displayState: "ready",
		reasonCodes: uniqueReasonCodes(projection.reasonCodes.length > 0 ? projection.reasonCodes : READY_REASON_CODES)
	};
};
const assertPrepareOutputHasNoBindingWriteAction = (input) => {
	for (const step of input.nextSteps ?? []) {
		if (!FORBIDDEN_BINDING_STEP_KINDS.has(step.kind)) continue;
		throw new Error(`prepare.contract_invalid: Prepare output must not include Binding write action: ${step.kind}`);
	}
};
const assertPrepareOutputHasNoExecutableWorkAction = (input) => {
	for (const step of input.nextSteps ?? []) {
		if (!FORBIDDEN_EXECUTABLE_WORK_STEP_KINDS.has(step.kind)) continue;
		throw new Error(`prepare.contract_invalid: Prepare output must not include executable work action: ${step.kind}`);
	}
};
//#endregion
//#region src/profile-setup.ts
function resolveSelectableCandidates(candidates) {
	return candidates.filter(isSelectableLocalAgentCandidate).sort((left, right) => left.agentId.localeCompare(right.agentId));
}
function isSelectableLocalAgentCandidate(candidate) {
	return candidate.targetKind === "builtin" && candidate.registered && candidate.enabled && candidate.lifecycleStatus !== "suspended" && candidate.selectable;
}
//#endregion
//#region src/retry-policy.ts
const DEFAULT_DISPATCH_MAX_ATTEMPTS = 3;
const DEFAULT_UNKNOWN_ERROR_MAX_ATTEMPTS = 3;
const DEFAULT_DISPATCH_OFFLINE_MAX_ATTEMPTS = 3;
const DEFAULT_DISPATCH_RETRY_BACKOFF_SECONDS = [
	1,
	3,
	10,
	30,
	60,
	120
];
const DEFAULT_DISPATCH_OFFLINE_RETRY_BACKOFF_SECONDS = [
	3,
	10,
	30,
	60
];
const DEFAULT_DISPATCH_RETRY_JITTER_RATIO = .2;
const shouldRetryByAttemptBudget = (input) => {
	const maxAttempts = input.maxAttempts ?? 3;
	return input.attempt < maxAttempts;
};
const computeRetryDelaySeconds = (input) => {
	const backoffSeconds = input.backoffSeconds ?? DEFAULT_DISPATCH_RETRY_BACKOFF_SECONDS;
	const jitterRatio = input.jitterRatio ?? .2;
	const attemptIndex = Math.max(1, input.attempt) - 1;
	const fallbackDelaySeconds = backoffSeconds.at(-1) ?? 120;
	const delayBaseSeconds = backoffSeconds[Math.min(attemptIndex, backoffSeconds.length - 1)] ?? fallbackDelaySeconds;
	const random = input.random ?? Math.random;
	const normalizedRandom = Math.min(1, Math.max(0, random()));
	const maxJitterSeconds = delayBaseSeconds * jitterRatio;
	const jitterSeconds = (normalizedRandom * 2 - 1) * maxJitterSeconds;
	return Math.max(1, Math.round(delayBaseSeconds + jitterSeconds));
};
//#endregion
//#region src/runtime-registry-contract.ts
const createCanonicalRuntimeLookupKey = (lookupInput) => {
	return JSON.stringify({
		ownerKind: lookupInput.owner.kind,
		ownerId: lookupInput.owner.id,
		deviceId: lookupInput.deviceId,
		lane: lookupInput.lane
	});
};
const getTimestamp = (value, fieldName) => {
	const timestamp = value instanceof Date ? value.getTime() : Date.parse(value);
	if (Number.isNaN(timestamp)) throw new Error(`runtime.agent_controller_report_time_invalid: ${fieldName} is invalid`);
	return timestamp;
};
const isAgentControllerReportStale = ({ now, report }) => {
	return getTimestamp(report.expiresAt, "expiresAt") <= getTimestamp(now, "now");
};
const isAgentControllerReportOutOfOrder = ({ currentAgentControllerReport, nextAgentControllerReport }) => {
	if (!currentAgentControllerReport) return false;
	return getTimestamp(nextAgentControllerReport.observedAt, "observedAt") < getTimestamp(currentAgentControllerReport.observedAt, "currentObservedAt");
};
const createRuntimeRegistryContractProjection = (record) => {
	return {
		wakeLoopServiceRuntimeId: record.wakeLoopServiceRuntimeId,
		currentAgentControllerReport: record.currentAgentControllerReport
	};
};
const createInMemoryRuntimeRegistryContractFixture = () => {
	const recordsByLookupKey = /* @__PURE__ */ new Map();
	return {
		acceptAgentControllerReport: ({ lookupInput, now, report }) => {
			const lookupKey = createCanonicalRuntimeLookupKey(lookupInput);
			const existingRecord = recordsByLookupKey.get(lookupKey);
			if (!existingRecord) throw new Error("runtime.registry_record_missing: cannot accept agent controller report for unregistered runtime");
			if (isAgentControllerReportStale({
				lookupInput,
				now,
				report
			})) return {
				status: "rejected",
				reasonCodes: ["runtime.agent_controller_report_stale"],
				currentAgentControllerReport: existingRecord.currentAgentControllerReport
			};
			if (isAgentControllerReportOutOfOrder({
				currentAgentControllerReport: existingRecord.currentAgentControllerReport,
				nextAgentControllerReport: report
			})) return {
				status: "rejected",
				reasonCodes: ["runtime.agent_controller_report_out_of_order"],
				currentAgentControllerReport: existingRecord.currentAgentControllerReport
			};
			existingRecord.currentAgentControllerReport = report;
			return {
				status: "accepted",
				reasonCodes: [],
				currentAgentControllerReport: existingRecord.currentAgentControllerReport
			};
		},
		registerRuntime: ({ lookupInput, wakeLoopServiceRuntimeId }) => {
			const lookupKey = createCanonicalRuntimeLookupKey(lookupInput);
			const existingRecord = recordsByLookupKey.get(lookupKey);
			if (existingRecord) return {
				status: "reused",
				wakeLoopServiceRuntimeId: existingRecord.wakeLoopServiceRuntimeId
			};
			recordsByLookupKey.set(lookupKey, {
				lookupInput,
				wakeLoopServiceRuntimeId,
				currentAgentControllerReport: void 0
			});
			return {
				status: "registered",
				wakeLoopServiceRuntimeId
			};
		},
		readRuntime: (lookupInput) => {
			return recordsByLookupKey.get(createCanonicalRuntimeLookupKey(lookupInput));
		},
		readRuntimeProjection: (lookupInput) => {
			const record = recordsByLookupKey.get(createCanonicalRuntimeLookupKey(lookupInput));
			return record ? createRuntimeRegistryContractProjection(record) : void 0;
		}
	};
};
//#endregion
//#region src/task-result-delivery-semantic.ts
const SUCCESS_LIKE_TASK_RESULT_DELIVERY_SEMANTIC = {
	kind: "success_like",
	successLike: true
};
const FAILED_TASK_RESULT_DELIVERY_SEMANTIC = {
	kind: "failed_requires_classification",
	successLike: false
};
const isTaskResultStatusSuccessLike = (status) => status === "success" || status === "partial_success";
const resolveTaskResultDeliverySemantic = (input) => isTaskResultStatusSuccessLike(input.payloadStatus) ? SUCCESS_LIKE_TASK_RESULT_DELIVERY_SEMANTIC : FAILED_TASK_RESULT_DELIVERY_SEMANTIC;
//#endregion
//#region src/wake-stop-eligibility.ts
function resolveWakeStopEligibility(input) {
	const capabilities = input.agentControllerInterface?.capabilities ?? null;
	if (!capabilities) return {
		controlMode: null,
		reasonCode: "agent_controller_interface.missing",
		reliability: "unsupported",
		status: "not_eligible"
	};
	if (capabilities.cancel) return {
		controlMode: "cancel",
		reasonCode: null,
		reliability: "native_cancel",
		status: "eligible"
	};
	if (capabilities.interrupt) return {
		controlMode: "interrupt",
		reasonCode: null,
		reliability: "native_interrupt",
		status: "eligible"
	};
	return {
		controlMode: null,
		reasonCode: "agent_controller_interface.stop_unsupported",
		reliability: "unsupported",
		status: "not_eligible"
	};
}
//#endregion
export { DEFAULT_AGENT_CONTROLLER_PERMISSION_MODE, DEFAULT_AGENT_EXECUTION_TIMEOUT_MS, DEFAULT_DISPATCH_FINALIZATION_TIMEOUT_MS, DEFAULT_DISPATCH_MAX_ATTEMPTS, DEFAULT_DISPATCH_OFFLINE_MAX_ATTEMPTS, DEFAULT_DISPATCH_OFFLINE_RETRY_BACKOFF_SECONDS, DEFAULT_DISPATCH_RETRY_BACKOFF_SECONDS, DEFAULT_DISPATCH_RETRY_JITTER_RATIO, DEFAULT_DISPATCH_START_TIMEOUT_MS, DEFAULT_DISPATCH_WAIT_WINDOW_MS, DEFAULT_IDEMPOTENCY_WINDOW_MS, DEFAULT_LOCAL_ADAPTER_PROCESS_TIMEOUT_MS, DEFAULT_TASK_LIFECYCLE_HEARTBEAT_INTERVAL_MS, DEFAULT_TASK_PROGRESS_EVIDENCE_STALE_AFTER_MS, DEFAULT_UNKNOWN_ERROR_MAX_ATTEMPTS, DUPLICATE_REPLY_CONVERSATION_CONTEXT, DUPLICATE_REPLY_CONVERSATION_REASON, DUPLICATE_REPLY_CONVERSATION_TOOLTIP, HOSTED_CENTER_AGENT_CONTROLLER_REF, HOSTED_CENTER_AGENT_CONTROLLER_REF_ID, MAX_DISPATCH_WAIT_WINDOW_MS, agentControllerPermissionModeRequiresExplicitConfirmation, assertAgentProfileBindingTruthWriterAllowed, assertAuthoritativeReadyWakeLoopServiceRuntimeId, assertPrepareOutputHasNoBindingWriteAction, assertPrepareOutputHasNoExecutableWorkAction, buildAgentControllerRef, buildHealthView, buildIdempotencyKey, classifyAgentWakeAvailabilityReason, computeRetryDelaySeconds, createAgentControllerInterfaceDisplayRows, createAgentExecutionStatusDisplayRows, createAgentProfileBindingAgentControllerPermissionMode, createAgentProfileBindingDisplayProjection, createAgentProfileBindingExecutionTarget, createAgentProfileBindingModelSelection, createAuthorityWakeLoopServiceRuntimeId, createInMemoryRuntimeRegistryContractFixture, createRuntimeIdentityKey, createRuntimeLookupInput, emitHealthFinding, formatAgentControllerInterfaceSummary, getAgentControllerPermissionModeDisplayMetadata, getAgentControllerPermissionModeFieldLabel, getAgentControllerVersionAdvisoryPresentation, hasRuntimeAdapterCapabilityForRef, isAgentExecutionLocalReadinessRefreshingReasonCode, isAgentExecutionReadyOrConnecting, isAgentExecutionRouteStatusUpdatingReasonCode, isAgentExecutionSelfHealingReadiness, isAgentExecutionSelfHealingReasonCode, isLeaseMatch, isTaskResultStatusSuccessLike, listAgentControllerPermissionModeOptions, parseAgentControllerRef, projectLocalAgentCatalogFromCapabilities, resolveAgentControllerInterfaceDescription, resolveAgentControllerPermissionMode, resolveAgentControllerPermissionModeForController, resolveAgentControllerRefFromAgentTarget, resolveAgentExecutionStatus, resolveAgentProfileBindingCommandEligibility, resolveAgentProfileBindingTruthWriteBoundary, resolveAgentProfileEditCapabilities, resolveAgentWakeAvailability, resolveAgentWakeAvailabilityDisplayCategory, resolveClaimEligibility, resolveConversationPolicyFromModelSelection, resolveEvidenceSyncHealth, resolveHostedAgentWakeAvailability, resolveLeaseArbitration, resolveNativeAgentControllerPermissionModeForController, resolvePrepareReadinessFromAgentControllerReports, resolvePrepareReadinessSummary, resolveSelectableCandidates, resolveTaskResultDeliverySemantic, resolveWakeStopEligibility, runtimeAgentControllerRefMatchesAdapterSnapshot, shouldDedupeWithinWindow, shouldExpandAgentControllerInterface, shouldRetryByAttemptBudget, wakeLoopServiceRuntimeIdentityKeysEqual };

//# sourceMappingURL=index.js.map